import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Box, Typography, IconButton, Paper, Tabs, Tab, Menu, Button, CircularProgress, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ShareIcon from "@mui/icons-material/Share";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ViewInArIcon from "@mui/icons-material/ViewInAr";
import RectangleOutlinedIcon from "@mui/icons-material/RectangleOutlined";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import DomainDisabledIcon from "@mui/icons-material/DomainDisabled";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { colors, typography, layout } from "../../../styles/theme";
import { getUserLotDetail, createUserLot, deleteUserLot, getUserLotLists, getUserLotUnits, getUfAttributeSchema } from '../../../services/userLotsService';
import { getSharedLotDetail, getSharedUserLot } from '../../../services/userLotsShareService';
import { useAuth } from '../../../Context/AuthContext';
import { useActiveSpace } from '../../context/ActiveSpaceContext';
import {
  SPACE_GUEST_READ_FORBIDDEN_MESSAGE,
  pickSpaceMirrorUnitDetail,
  pickUfPermissionFields,
  resolveSpaceGuestUnitAccess,
  resolveUfDraftDiscard,
  stripSpaceGuestUfPayload,
  resolvePrivateBlockTitle,
  resolveSpaceGuestWriteError,
  resolveSpaceMirrorReadOwner,
} from '../../utils/spaceMirrorLots';
import {
  readUfSaveWarnings,
  resolveUfPropertyErrorFieldId,
  resolveUfPropertyTypes,
} from '../../utils/ufPropertyFields';
import { DOCK_SCROLLBAR_SX } from './Tabs/formBlockUi';
import { LotInfoTab, DetailsTab, UnitsTabList, NearbyAttractionsTab } from './Tabs';
import { UNIT_HIGHLIGHT_MS, shouldLeaveToUnitListAfterSave } from './Tabs/unitHighlight';
import { filterUnitsByListId } from './Tabs/UnitListView';
import { attachUnitsCommercial, extractUnitsCommercial, onCommercialChanged } from '../../../Leads/commercialSummary';
import ShareLotDialog from '../../../Components/ShareLotDialog';
import CollectionInquiryForm, {
  COLLECTION_INQUIRY_FORM_ID,
} from '../../../Components/CollectionInquiryForm';
import {
  resolveSharedListInquiryTarget,
  resolveInquiryPanelContext,
  resolveSharedLotInquiryTarget,
} from '../../utils/collectionInquiryForm';
import { getUnitHeaderTitle, getUnitPublicTitle } from '../../utils/userLotUnits';
import RecordLeadsSection from '../../../Leads/components/RecordLeadsSection';
import RecordCampaignsSection from '../../../MetaAds/components/RecordCampaignsSection';
import MarketComparisonCard from '../../../MarketComparison/components/MarketComparisonCard';
import { marketRevisionOf } from '../../../MarketComparison/marketComparison';
import { geometryCenter, normalizeMarketCenter } from '../../../MarketComparison/marketComparablesMap';
import { getLotRepresentativeLatLng } from '../../hooks/useLotSelection';
import { resolveLotContentTitle } from '../../utils/listingPresentationUtils';
import FeedbackModal from '../FeedbackModal/FeedbackModal';
import { showroomInquiryHeaderButtonSx } from '../../../Showroom/Showroom.styles';
import PanelShell from '../PanelShell/PanelShell';
import PanelHeader, { panelCloseIconButtonSx, panelHeaderDividerSx } from '../PanelShell/PanelHeader';
import ActionMenuItem, { ACTION_MENU_LIST_PROPS, ACTION_MENU_PAPER_PROPS } from '../ActionMenuItem';
import { getListFilterChipSx, listFilterChipsRowSx } from '../UserLotListsPanel/UserLotListsPanel.styles';
import {
  PANEL_SECTION_TITLE_SX,
  LotInfoPanelSkeleton,
  UnitListPanelSkeleton,
} from './Tabs/formBlockUi';
import { pickNonEmptyBoundary } from '../../utils/enrichSharedLotsForMap';
import { getContrastTextColor, withAlpha } from '../../utils/sharedBranding';
import { pickServiceDeficitFields } from '../../utils/serviceDeficitDisplay';
import { toast } from '../../../Components/Toast';
import { confirmDialog } from '../../../Components/ConfirmDialog';
import { useScrollEdgeFade, getScrollEdgeFadeSx } from '../../../hooks/useScrollEdgeFade';
import { getLotUserContent } from '../../../services/lotUserContentService';
import { buildLotUserContentSharePreview } from '../../utils/lotUserContentSharePreview';
import {
  emptyUserLotForm,
  mapUserLotToForm,
  buildUserLotPayload,
  buildUserLotListingCorePayload,
} from '../../utils/userLotFormUtils';
import {
  UF_WIZARD_STEP,
  UF_BUILDING_TYPES_FALLBACK,
  clearAttributeFormValues,
  extractApiErrorMessage,
  hasUfAttributeDisplayData,
  mergeDetailIntoAttributeForm,
  normalizeBuildingType,
  resolveBuildingTypes,
  resolveInitialWizardStep,
} from '../../utils/ufAttributeFormUtils';
import { useLotFunctionalUnits } from '../../hooks/useLotFunctionalUnits';
import {
  getAlreadyAnnotatedUserLotId,
  getOfficialUnitFromUserLot,
  getOfficialUnitNumber,
  applyOfficialBuiltSurfaceToForm,
  parseOfficialBuiltSurfaceM2,
  scopeOfficialUnitsToOwnerOverlays,
} from '../../utils/lotFunctionalUnits';

function buildPublicLotUrl(lotId) {
  const { origin } = window.location;
  return `${origin}/#/shared-lot/${encodeURIComponent(lotId)}`;
}

const SIDEBAR_RAIL_WIDTH = layout.sidebarRailWidth;
const PANEL_BG = '#1c1c1c';
const PANEL_BORDER = '#4a4a4a';

const dockHeaderIconButtonSx = {
  color: '#E8F4F4',
  p: 0.5,
  '&:hover': { color: '#FFFFFF', bgcolor: 'rgba(255,255,255,0.06)' },
};

const dockActionMenuTriggerSx = (isOpen) => ({
  color: isOpen ? colors.primary : '#E8F4F4',
  p: 0.5,
  '&:hover': {
    color: colors.primary,
    bgcolor: 'rgba(255,255,255,0.06)',
  },
});

const MarkerMenuDot = ({ active = false }) => (
  <Box
    sx={{
      width: 8,
      height: 8,
      borderRadius: '50%',
      bgcolor: active ? colors.primary : '#D4D4D4',
      flexShrink: 0,
    }}
  />
);

const dockActionMenuPaperProps = {
  sx: {
    ...ACTION_MENU_PAPER_PROPS.sx,
    minWidth: '196px',
    width: '196px',
  },
};

const BUILDING_VIEW_OPTIONS = [
  { value: 'full', label: 'Modelo 3D', Icon: ViewInArIcon },
  { value: 'footprints', label: 'Planta', Icon: RectangleOutlinedIcon },
  { value: 'hidden', label: 'Ocultar', Icon: VisibilityOffIcon },
];

const BUILDING_VIEW_CHIP_DISABLED_SX = {
  opacity: 1,
  color: colors.border,
  borderColor: colors.border,
  bgcolor: 'transparent',
  cursor: 'not-allowed',
  pointerEvents: 'none',
  '&:hover': {
    bgcolor: 'transparent',
    borderColor: colors.border,
    boxShadow: 'none',
  },
};

const getBrandedBuildingChipSx = (isActive, primary) => {
  const base = getListFilterChipSx(isActive);
  if (!primary) return base;
  const primarySoft = withAlpha(primary, 0.22);
  return {
    ...base,
    borderColor: primary,
    bgcolor: isActive ? primary : 'transparent',
    color: isActive ? getContrastTextColor(primary) : primary,
    '&:hover': {
      bgcolor: isActive ? primary : primarySoft,
      borderColor: primary,
      boxShadow: 'none',
      filter: isActive ? 'brightness(1.06)' : 'none',
    },
  };
};

const renderBuildingViewChips = ({
  disabled = false,
  value = null,
  onChange,
  ariaLabel,
  accentColor = null,
}) => (
  <Box
    sx={{ ...listFilterChipsRowSx, mb: 0, ml: 0, flex: 1 }}
    role="group"
    aria-label={ariaLabel}
  >
    {BUILDING_VIEW_OPTIONS.map(({ value: optionValue, label, Icon }) => {
      const isActive = !disabled && value === optionValue;
      return (
        <Button
          key={optionValue}
          disableElevation
          aria-disabled={disabled}
          tabIndex={disabled ? -1 : 0}
          onClick={disabled ? undefined : () => onChange?.(optionValue)}
          aria-pressed={isActive}
          sx={{
            ...getBrandedBuildingChipSx(isActive, accentColor),
            ...(disabled ? BUILDING_VIEW_CHIP_DISABLED_SX : {}),
          }}
        >
          <Icon sx={{ fontSize: 14, color: 'inherit' }} />
          {label}
        </Button>
      );
    })}
  </Box>
);
/** Referencia estable: evita que el default `[]` reinicie el dock en cada render. */
export const EMPTY_SHARED_LIST_UNITS = Object.freeze([]);

function buildSharedLotUserLotForm(data) {
  return mapUserLotToForm(data, { observationsDefault: '' });
}

function mergeSharedLotIntoEditedInfo(base, incoming) {
  const prev = base || {};
  const data = incoming || {};
  return {
    ...prev,
    street_name: data.street_name ?? prev.street_name,
    street_number: data.street_number ?? prev.street_number,
    neighborhood: data.neighborhood_name ?? data.neighborhood ?? prev.neighborhood,
    municipality: data.municipality_name ?? data.municipality ?? prev.municipality,
    locality: data.locality_name ?? data.locality ?? prev.locality,
    lat: data.lat ?? data.latitude ?? prev.lat,
    lng: data.lng ?? data.longitude ?? data.lon ?? prev.lng,
    boundary: pickNonEmptyBoundary(data.boundary, data.lot_boundary, prev.boundary),
    name: data.lot_name ?? prev.name,
    surface: data.lot_surface ?? data.surface ?? prev.surface,
    urbansurface: data.lot_urbansurface ?? data.urbansurface ?? prev.urbansurface,
    built_footprint_m2: data.built_footprint_m2 ?? data.lot_built_footprint_m2 ?? prev.built_footprint_m2,
    max_footprint_m2: data.max_footprint_m2 ?? data.lot_max_footprint_m2 ?? prev.max_footprint_m2,
    max_buildable_m2: data.max_buildable_m2 ?? data.lot_max_buildable_m2 ?? prev.max_buildable_m2,
    valuation: data.lot_valuation ?? data.valuation ?? prev.valuation,
    state: data.state ?? prev.state ?? null,
    type: data.lot_type ?? data.type ?? prev.type ?? null,
    zoning_zone: data.zoning_zone ?? data.lot_zoning_zone ?? prev.zoning_zone,
    zoning_perfil: data.zoning_perfil ?? data.lot_zoning_perfil ?? prev.zoning_perfil,
    flood_risk_pct: data.flood_risk_pct ?? data.lot_flood_risk_pct ?? prev.flood_risk_pct,
    chronic_poverty_pct: data.chronic_poverty_pct ?? data.lot_chronic_poverty_pct ?? prev.chronic_poverty_pct,
    population_density: data.population_density ?? data.lot_population_density ?? prev.population_density,
    front_highway_type: data.front_highway_type ?? data.lot_front_highway_type ?? prev.front_highway_type,
    street_position: data.street_position ?? data.lot_street_position ?? prev.street_position,
    neighborhood_type: data.neighborhood_type ?? data.lot_neighborhood_type ?? prev.neighborhood_type,
    lot_shape: data.lot_shape ?? prev.lot_shape,
    front_azimuth: data.front_azimuth ?? data.lot_front_azimuth ?? prev.front_azimuth,
    ...pickServiceDeficitFields(data, prev),
  };
}

// ─── Component ─────────────────────────────────────────────────────────────────
const normalizeSharedListUnits = (rows) => (
  (Array.isArray(rows) ? rows : []).map((unit) => ({
    ...unit,
    id: unit.id || unit.user_lot_id,
    user_lot_id: unit.user_lot_id || unit.id,
  }))
);

const DockPanel = ({ uiLeftInset = SIDEBAR_RAIL_WIDTH, polygonInfo, onClose, onUpdate, isLoading, loadError, isFavorite, onToggleFavorite, openInEditMode, onEditModeConsumed, initialUserLotId = null, onInitialUserLotIdConsumed, initialOpenLotContent = false, onInitialOpenLotContentConsumed, readOnly = false, sharedToken = '', sharedUserLotId = null, sharedUserLotToken = '', publicLotView = false, listContextLabel = '', restrictToListId = null, lotBuildingDisplayMode = 'hidden', onLotBuildingDisplayModeChange, lotBuildingStatus = 'idle', sharedLotDetail = null, skipSharedLotDetailFetch = false, lotDetailLoading = false, sharedListUnits = EMPTY_SHARED_LIST_UNITS, sharedOpenUnitsList = false, sharedListIncludesLot = false, sharedListLotUserContent = null, accentColor = null, shellBottomInset = 0, shellForceMobile = false, shellFullWidth = false, shellTopInset = 0, shellNotice = null, collectionCanEdit = false, viewerCanManage = false, onOpenLead = undefined, initialUserLotEdit = true, onShowMarketComparables = undefined }) => {
  const { user } = useAuth();
  const { activeOwnerUserId, activeSpace, isViewingSharedSpace } = useActiveSpace();
  const currentUserId = user?.sub;
  // Resumen comercial solo en vistas del dueño / editor invitado (nunca share ni mapa público).
  const canLoadCommercial = Boolean(currentUserId) && !readOnly && !sharedToken
    && !sharedUserLotToken && !publicLotView;
  // Espacio ajeno: invitado. Escribe en el espacio del owner con `owner_user_id`
  // (contenido del lote, sumar media, crear lista). UF: alta con el mismo flujo
  // del dueño (borrador incluido) y, en las que creó él (`can_edit_all` /
  // `can_delete`, mig 082), edición completa y borrado; en las demás, regla
  // acotada. Compartir lote (F3) va con el owner; marcadores siguen ocultos.
  // Ver espacios-invite.md.
  const isSpaceGuest = isViewingSharedSpace && !readOnly;
  // F1: el invitado lee contenido / UF / media del owner (`?owner_user_id=`).
  // En el propio espacio y en shares por token → null.
  const spaceReadOwnerUserId = resolveSpaceMirrorReadOwner({
    isViewingSharedSpace,
    activeOwnerUserId,
    readOnly,
  });
  // F2: mismo owner en las escrituras aditivas (body o query según endpoint).
  const spaceWriteOwnerUserId = spaceReadOwnerUserId;
  // Owner vigente para descartar respuestas de una escritura si el espacio
  // cambió mientras tanto (restore sin cierre del Dock).
  const spaceWriteOwnerRef = useRef(spaceWriteOwnerUserId);
  spaceWriteOwnerRef.current = spaceWriteOwnerUserId;
  // Título del bloque privado: "Solo en tu espacio" / label del owner; shares → "Solo vos".
  const privateBlockTitle = resolvePrivateBlockTitle({
    readOnly,
    spaceReadOwnerUserId,
    activeSpace,
  });

  // Acento opcional: solo lo usan las vistas compartidas con marca propia.
  const panelAccent = accentColor || colors.primary;
  const brandChipAccent = accentColor || null;

  const polygonId = polygonInfo?.id ?? null;
  const polygonName = polygonInfo?.name ?? '';
  const polygonProvince = polygonInfo?.province ?? '';
  const polygonDepartment = polygonInfo?.department ?? '';
  const polygonValuation = polygonInfo?.valuation ?? null;
  const polygonUrbanSurface = polygonInfo?.urbansurface ?? null;
  const polygonSurface = polygonInfo?.surface ?? null;
  const polygonBuiltFootprintM2 = polygonInfo?.built_footprint_m2 ?? null;
  const polygonMaxFootprintM2 = polygonInfo?.max_footprint_m2 ?? null;
  const polygonMaxBuildableM2 = polygonInfo?.max_buildable_m2 ?? null;
  const polygonNeighborhood = polygonInfo?.neighborhood_name ?? polygonInfo?.neighborhood ?? '';
  const polygonLocality = polygonInfo?.locality_name ?? polygonInfo?.locality ?? polygonInfo?.city ?? '';
  const polygonMunicipality = polygonInfo?.municipality_name ?? polygonInfo?.municipality ?? '';
  const polygonStreet = polygonInfo?.street ?? '';
  const polygonState = polygonInfo?.state ?? '';
  const polygonType = polygonInfo?.type ?? '';
  const polygonLat = polygonInfo?.lat ?? null;
  const polygonLng = polygonInfo?.lng ?? null;
  const polygonQuota = polygonInfo?.quota ?? null;
  const polygonZoningZone = polygonInfo?.zoning_zone ?? null;
  const polygonZoningPerfil = polygonInfo?.zoning_perfil ?? null;
  const polygonFloodRiskPct = polygonInfo?.flood_risk_pct ?? null;
  const polygonChronicPovertyPct = polygonInfo?.chronic_poverty_pct ?? null;
  const polygonPopulationDensity = polygonInfo?.population_density ?? null;
  const polygonFrontHighwayType = polygonInfo?.front_highway_type ?? polygonInfo?.lot_front_highway_type ?? null;
  const polygonStreetPosition = polygonInfo?.street_position ?? polygonInfo?.lot_street_position ?? null;
  const polygonNeighborhoodType = polygonInfo?.neighborhood_type ?? polygonInfo?.lot_neighborhood_type ?? null;
  const polygonLotShape = polygonInfo?.lot_shape ?? null;
  const polygonFrontAzimuth = polygonInfo?.front_azimuth ?? polygonInfo?.lot_front_azimuth ?? null;
  const polygonWaterNetworkDeficitPct = polygonInfo?.water_network_deficit_pct ?? null;
  const polygonSewerDeficitPct = polygonInfo?.sewer_deficit_pct ?? null;
  const polygonCookingFuelDeficitPct = polygonInfo?.cooking_fuel_deficit_pct ?? null;
  const polygonBoundary = Array.isArray(polygonInfo?.boundary) ? polygonInfo.boundary : null;

  const isSharedListView = readOnly && (sharedToken || skipSharedLotDetailFetch) && !sharedUserLotToken;
  const sharedListUnitsKey = useMemo(
    () => (Array.isArray(sharedListUnits) ? sharedListUnits : [])
      .map((unit) => String(unit?.id || unit?.user_lot_id || ''))
      .filter(Boolean)
      .join(','),
    [sharedListUnits],
  );
  const normalizedSharedListUnits = useMemo(
    () => normalizeSharedListUnits(sharedListUnits),
    // Contenido (ids), no identidad del array: evita reset del dock en cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sharedListUnitsKey],
  );
  const normalizedSharedListUnitsRef = useRef(normalizedSharedListUnits);
  normalizedSharedListUnitsRef.current = normalizedSharedListUnits;

  const [editedInfo, setEditedInfo] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [shareLotDialogOpen, setShareLotDialogOpen] = useState(false);
  const [sharePreselectedUnitIds, setSharePreselectedUnitIds] = useState([]);
  // Cambio de espacio: el diálogo de compartir es del espacio anterior.
  useEffect(() => {
    setShareLotDialogOpen(false);
    setSharePreselectedUnitIds((prev) => (prev.length ? [] : prev));
  }, [spaceWriteOwnerUserId]);
  const [parcelFeedbackOpen, setParcelFeedbackOpen] = useState(false);

  const [userLotData, setUserLotData] = useState(undefined);
  const [userLotChecked, setUserLotChecked] = useState(false);
  const [loadingUserLot, setLoadingUserLot] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [userLotLists, setUserLotLists] = useState([]);

  // Multi-unidades
  const [units, setUnits] = useState([]);
  const [selectedUnitId, setSelectedUnitId] = useState(null);
  // Resumen comercial (paso 6): aparte de `units` / `userLotData` para que las
  // recargas sin include (guardar, borrar) no lo borren. Ver commercial-summary.md.
  const [unitsCommercial, setUnitsCommercial] = useState(null);
  const [ufCommercial, setUfCommercial] = useState(null);
  const [isNewUnit, setIsNewUnit] = useState(false);
  // 'list' = listado de unidades | 'detail' = formulario de una unidad
  const [unitView, setUnitView] = useState('list');
  const [sharedLotUserContent, setSharedLotUserContent] = useState(null);
  /** UF oficial que se está anotando (POST con `unit_number`). Null = UF propia. */
  const [pendingOfficialUnit, setPendingOfficialUnit] = useState(null);

  const fetchOfficialUnits = Boolean(
    polygonId
    && currentUserId
    && !readOnly
    && !restrictToListId
    && activeTab === 1,
  );
  // Espacio ajeno: `?owner_user_id=` → `user_lot_id` de las UF del owner (cache por lote + owner).
  const {
    officialUnits: rawOfficialUnits,
    officialUnitsLoading,
    officialUnitsErrorStatus,
    reloadOfficialUnits,
  } = useLotFunctionalUnits({
    lotId: polygonId,
    enabled: fetchOfficialUnits,
    ownerUserId: spaceReadOwnerUserId,
  });
  // Espacio ajeno: solo vínculos a UF del owner (nunca una del invitado).
  const officialUnits = useMemo(
    () => (spaceReadOwnerUserId
      ? scopeOfficialUnitsToOwnerOverlays(rawOfficialUnits, units)
      : rawOfficialUnits),
    [spaceReadOwnerUserId, rawOfficialUnits, units],
  );
  // 403 (ya no es miembro): aviso claro; el tab sigue con las UF que haya.
  useEffect(() => {
    if (spaceReadOwnerUserId && officialUnitsErrorStatus === 403) {
      toast.error({ title: 'Error', message: SPACE_GUEST_READ_FORBIDDEN_MESSAGE });
    }
  }, [spaceReadOwnerUserId, officialUnitsErrorStatus, polygonId]);

  const LoadingSkeleton = ({ tab }) => (
    tab === 'details' ? <UnitListPanelSkeleton /> : <LotInfoPanelSkeleton />
  );

  const [userLotForm, setUserLotForm] = useState(emptyUserLotForm);
  const [ufWizardStep, setUfWizardStep] = useState(UF_WIZARD_STEP.BUILDING_TYPE);
  const [ufAttributeSchema, setUfAttributeSchema] = useState(null);
  const [ufBuildingTypes, setUfBuildingTypes] = useState(
    () => resolveUfPropertyTypes(null, UF_BUILDING_TYPES_FALLBACK),
  );
  const [ufSchemaLoading, setUfSchemaLoading] = useState(false);
  /** True mientras el paso 1 avanza al 2 (save + schema): UI optimista con skeleton. */
  const [ufListingBootstrapLoading, setUfListingBootstrapLoading] = useState(false);
  /** Borrador solo con building_type: si se abandona en paso 2 sin Continuar, se elimina. */
  const ufBuildingTypeDraftPendingRef = useRef(false);
  /** Owner del espacio donde se creó el borrador (null = propio): se borra con ese owner aunque cambie el espacio. */
  const ufDraftOwnerRef = useRef(null);
  /** UF creada en esta sesión del wizard (el paso 1 ya la guardó como borrador): al Guardar final vuelve al listado. */
  const ufCreatedInWizardRef = useRef(false);
  /** UF recién creada que se resalta un momento al volver al listado. */
  const [highlightUnitId, setHighlightUnitId] = useState(null);
  useEffect(() => {
    if (highlightUnitId == null) return undefined;
    const timer = setTimeout(() => setHighlightUnitId(null), UNIT_HIGHLIGHT_MS);
    return () => clearTimeout(timer);
  }, [highlightUnitId]);
  const userLotDataRef = useRef(userLotData);
  userLotDataRef.current = userLotData;
  const panelContentScrollRef = useRef(null);
  const { scrollRef: fadeRef, fadeTop, fadeBottom } = useScrollEdgeFade();
  const setPanelContentScrollNode = useCallback((node) => {
    panelContentScrollRef.current = node;
    fadeRef(node);
  }, [fadeRef]);
  const prevUfWizardStepRef = useRef(UF_WIZARD_STEP.BUILDING_TYPE);

  const scrollToInquiry = useCallback(() => {
    const form = document.getElementById(COLLECTION_INQUIRY_FORM_ID);
    if (!form) return;
    form.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  // Cada cambio de paso del wizard (Continuar, Atrás o stepper) vuelve el body de
  // DetailsTab (panelContentScrollRef) arriba. Tipo → Publicación avanza optimista con
  // skeleton: el contenido real (con adjuntos) se monta cuando termina el bootstrap,
  // así que también se vuelve arriba en ese momento.
  const prevUfListingBootstrapRef = useRef(false);
  useEffect(() => {
    const prevStep = prevUfWizardStepRef.current;
    const prevBootstrap = prevUfListingBootstrapRef.current;
    prevUfWizardStepRef.current = ufWizardStep;
    prevUfListingBootstrapRef.current = ufListingBootstrapLoading;
    if (readOnly || activeTab !== 1 || !isEditing) return;

    const stepChanged = prevStep !== ufWizardStep;
    const bootstrapFinished = prevBootstrap && !ufListingBootstrapLoading;
    if (!stepChanged && !bootstrapFinished) return;

    const scrollEl = panelContentScrollRef.current;
    if (!scrollEl) return;
    requestAnimationFrame(() => {
      scrollEl.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }, [ufWizardStep, ufListingBootstrapLoading, activeTab, isEditing, readOnly]);

  const refreshUserLotLists = useCallback(async () => {
    if (!currentUserId) return;
    try {
      const spaceOwnerId = activeOwnerUserId ? String(activeOwnerUserId).trim() : '';
      const res = await getUserLotLists(
        spaceOwnerId ? { ownerUserId: spaceOwnerId } : undefined,
      );
      if (Array.isArray(res.data)) setUserLotLists(res.data);
      else setUserLotLists([]);
    } catch (e) {
      console.error('Error fetching user lot lists:', e);
      setUserLotLists([]);
    }
  }, [currentUserId, activeOwnerUserId]);

  const loadUfSchema = useCallback(async (buildingType) => {
    const normalizedType = normalizeBuildingType(buildingType);
    if (!normalizedType) {
      setUfAttributeSchema(null);
      return null;
    }
    setUfSchemaLoading(true);
    try {
      const res = await getUfAttributeSchema(normalizedType);
      if (res?.data) {
        setUfAttributeSchema(res.data);
        return res.data;
      }
      setUfAttributeSchema(null);
      return null;
    } catch (e) {
      console.error('Error loading UF attribute schema:', e);
      setUfAttributeSchema(null);
      return null;
    } finally {
      setUfSchemaLoading(false);
    }
  }, []);

  const loadUfBuildingTypes = useCallback(async () => {
    try {
      const res = await getUfAttributeSchema();
      // Paso 1: los 7 tipos de property_fields.property_type (fallback building_types).
      setUfBuildingTypes(resolveUfPropertyTypes(res, resolveBuildingTypes(res)));
    } catch (e) {
      console.error('Error loading UF building types:', e);
      setUfBuildingTypes(resolveUfPropertyTypes(null, UF_BUILDING_TYPES_FALLBACK));
    }
  }, []);

  useEffect(() => {
    if (currentUserId && !readOnly) {
      loadUfBuildingTypes();
    }
  }, [currentUserId, readOnly, loadUfBuildingTypes]);

  const applyDetailWithSchema = useCallback(async (detail, baseForm) => {
    const buildingType = normalizeBuildingType(baseForm.building_type);
    const withOfficialSurface = (form) => applyOfficialBuiltSurfaceToForm(
      form,
      getOfficialUnitFromUserLot(detail),
    );
    if (!buildingType) {
      setUfAttributeSchema(null);
      return withOfficialSurface(baseForm);
    }
    const schema = await loadUfSchema(buildingType);
    if (!schema) return withOfficialSurface(baseForm);
    return withOfficialSurface({
      ...baseForm,
      ...mergeDetailIntoAttributeForm(detail, schema),
    });
  }, [loadUfSchema]);

  /** Hidrata form de UF en shares: valores + schema (labels) si hay atributos. */
  // Secuencia de hidratación: una respuesta de schema vieja (registro anterior)
  // no pisa el form del registro actual.
  const hydrateSharedSeqRef = useRef(0);
  const hydrateSharedUnitForm = useCallback(async (detail) => {
    hydrateSharedSeqRef.current += 1;
    const seq = hydrateSharedSeqRef.current;
    const baseForm = applyOfficialBuiltSurfaceToForm(
      buildSharedLotUserLotForm(detail),
      getOfficialUnitFromUserLot(detail),
    );
    setUserLotForm(baseForm);
    const buildingType = normalizeBuildingType(baseForm.building_type);
    if (!buildingType || !hasUfAttributeDisplayData(baseForm)) {
      setUfAttributeSchema(null);
      return baseForm;
    }
    const mergedForm = await applyDetailWithSchema(detail, baseForm);
    if (seq !== hydrateSharedSeqRef.current) return mergedForm;
    setUserLotForm(mergedForm);
    return mergedForm;
  }, [applyDetailWithSchema]);

  const handleSelectUnit = useCallback(async (unitId) => {
    if (!editedInfo?.id) return null;
    if (unitId === selectedUnitId) return userLotDataRef.current || null;

    // Lista / snapshot compartido: detalle por token (no getuserlotdetail autenticado).
    if (readOnly && sharedToken && !sharedUserLotToken) {
      const unit = units.find(
        (row) => String(row.id) === String(unitId)
          || String(row.user_lot_id) === String(unitId),
      );
      if (!unit) return null;
      setSelectedUnitId(unitId);
      setIsNewUnit(false);
      setIsEditing(false);
      setPendingOfficialUnit(null);
      setLoadingUserLot(true);
      try {
        const res = await getSharedLotDetail(sharedToken, { userLotId: unitId });
        if (res?.data) {
          setUserLotData(res.data);
          await hydrateSharedUnitForm(res.data);
          return res.data;
        }
        setUserLotData(null);
        return null;
      } catch (e) {
        console.error('Error loading shared list unit detail:', e);
        setUserLotData(null);
        return null;
      } finally {
        setLoadingUserLot(false);
      }
    }

    // Vista compartida por token: las unidades ya vienen en el snapshot (no hay getuserlotdetail).
    if (readOnly && sharedUserLotToken) {
      const unit = units.find(
        (row) => String(row.id) === String(unitId)
          || String(row.user_lot_id) === String(unitId),
      );
      if (!unit) return null;
      setSelectedUnitId(unitId);
      setIsNewUnit(false);
      setIsEditing(false);
      setPendingOfficialUnit(null);
      setUserLotData(unit);
      await hydrateSharedUnitForm(unit);
      return unit;
    }

    // Espacio ajeno (F1): las UF son del owner (`getuserlotunits?owner_user_id=`).
    // Detalle con `getuserlotdetail?owner_user_id=` (trae `observations` y media);
    // si falla (403 / 404 / API sin desplegar), la fila del listado, y la media
    // la piden UserLot* con el mismo owner. Permisos: `isSpaceGuest`, no `is_owner`.
    if (spaceReadOwnerUserId) {
      const unit = units.find(
        (row) => String(row.id) === String(unitId)
          || String(row.user_lot_id) === String(unitId),
      );
      if (!unit) return null;
      setSelectedUnitId(unitId);
      setIsNewUnit(false);
      setIsEditing(false);
      setPendingOfficialUnit(null);
      setLoadingUserLot(true);
      try {
        let detailRes = null;
        try {
          detailRes = await getUserLotDetail(editedInfo.id, unit.id || unit.user_lot_id || unitId, {
            ownerUserId: spaceReadOwnerUserId,
            includeCommercialSummary: canLoadCommercial,
          });
          setUfCommercial(detailRes?.data?.commercial_summary
            ? { id: String(unitId), summary: detailRes.data.commercial_summary }
            : null);
        } catch (e) {
          console.error('Error loading owner unit detail (space mirror):', e);
        }
        const detail = pickSpaceMirrorUnitDetail(detailRes, unit);
        setUserLotData(detail);
        const mergedForm = await applyDetailWithSchema(detail, mapUserLotToForm(detail));
        setUserLotForm(mergedForm);
        setUfWizardStep(resolveInitialWizardStep({
          isEditing: false,
          buildingType: mergedForm.building_type,
        }));
        return detail;
      } finally {
        setLoadingUserLot(false);
      }
    }

    setSelectedUnitId(unitId);
    setIsNewUnit(false);
    setIsEditing(false);
    setPendingOfficialUnit(null);
    setLoadingUserLot(true);
    try {
      const res = await getUserLotDetail(editedInfo.id, unitId, {
        includeCommercialSummary: canLoadCommercial,
      });
      setUfCommercial(res?.data?.commercial_summary
        ? { id: String(unitId), summary: res.data.commercial_summary }
        : null);
      if (res.data) {
        setUserLotData(res.data);
        const baseForm = mapUserLotToForm(res.data);
        const mergedForm = await applyDetailWithSchema(res.data, baseForm);
        setUserLotForm(mergedForm);
        setUfWizardStep(resolveInitialWizardStep({
          isEditing: false,
          buildingType: mergedForm.building_type,
        }));
        return res.data;
      }
      setUserLotData(null);
      return null;
    } catch (e) {
      console.error('Error loading unit detail:', e);
      setUserLotData(null);
      return null;
    } finally {
      setLoadingUserLot(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editedInfo?.id, selectedUnitId, readOnly, sharedToken, sharedUserLotToken, units, applyDetailWithSchema, hydrateSharedUnitForm, spaceReadOwnerUserId, canLoadCommercial]);

  const discardUfBuildingTypeDraftIfNeeded = useCallback(async () => {
    if (!ufBuildingTypeDraftPendingRef.current) return false;
    const draftId = userLotDataRef.current?.id;
    const draftOwnerUserId = ufDraftOwnerRef.current;
    ufBuildingTypeDraftPendingRef.current = false;
    ufDraftOwnerRef.current = null;
    ufCreatedInWizardRef.current = false;
    setUfListingBootstrapLoading(false);

    if (!draftId) {
      setUserLotData(null);
      setSelectedUnitId(null);
      setIsNewUnit(false);
      setPendingOfficialUnit(null);
      setUserLotForm(emptyUserLotForm());
      setUfAttributeSchema(null);
      return true;
    }

    try {
      // Espacio ajeno: el invitado borra su borrador con `owner_user_id` (mig 082).
      // Si cambió el espacio mientras tanto, no pisar las UF del nuevo.
      const plan = resolveUfDraftDiscard({
        draftOwnerUserId,
        currentOwnerUserId: spaceWriteOwnerRef.current,
      });
      await deleteUserLot(draftId, plan.deleteOptions);
      if (editedInfo?.id && plan.refreshUnits) {
        try {
          const unitsRes = await getUserLotUnits(editedInfo.id, { ownerUserId: draftOwnerUserId });
          const remaining = filterUnitsByListId(
            Array.isArray(unitsRes?.data) ? unitsRes.data : [],
            restrictToListId,
          );
          setUnits(remaining);
        } catch (_) {
          setUnits((prev) => prev.filter((unit) => unit.id !== draftId));
        }
      } else {
        setUnits((prev) => prev.filter((unit) => unit.id !== draftId));
      }
      reloadOfficialUnits();
      setUserLotData(null);
      setSelectedUnitId(null);
      setIsNewUnit(false);
      setPendingOfficialUnit(null);
      setUserLotForm(emptyUserLotForm());
      setUfAttributeSchema(null);
      refreshUserLotLists();
      return true;
    } catch (e) {
      console.error('Error discarding UF building type draft:', e);
      return false;
    }
  }, [editedInfo?.id, restrictToListId, refreshUserLotLists, reloadOfficialUnits]);

  /** Confirma antes de descartar el borrador del paso Tipo (Cancelar / lista / cerrar panel). */
  const confirmDiscardUfBuildingTypeDraftIfNeeded = useCallback(async () => {
    if (!ufBuildingTypeDraftPendingRef.current) {
      return { discarded: false, cancelled: false };
    }

    const result = await confirmDialog({
      title: '¿Salir sin completar la unidad?',
      description: 'Si salís ahora se perderán los datos cargados de esta unidad. Esta acción no se puede deshacer.',
      confirmLabel: 'Salir',
      cancelLabel: 'Cancelar',
      variant: 'danger',
    });

    if (!result?.isConfirmed) {
      return { discarded: false, cancelled: true };
    }

    const discarded = await discardUfBuildingTypeDraftIfNeeded();
    return { discarded, cancelled: false };
  }, [discardUfBuildingTypeDraftIfNeeded]);

  const handlePanelClose = useCallback(async () => {
    const { cancelled } = await confirmDiscardUfBuildingTypeDraftIfNeeded();
    if (cancelled) return;
    onClose?.();
  }, [confirmDiscardUfBuildingTypeDraftIfNeeded, onClose]);

  const handleAddUnit = useCallback((officialUnit = null) => {
    ufBuildingTypeDraftPendingRef.current = false;
    ufCreatedInWizardRef.current = false;
    setUfListingBootstrapLoading(false);
    setIsNewUnit(true);
    setSelectedUnitId(null);
    setUserLotData(null);
    setIsEditing(true);
    setPendingOfficialUnit(officialUnit || null);
    const unitNumber = getOfficialUnitNumber(officialUnit);
    const base = unitNumber
      ? { ...emptyUserLotForm(), unit_label: unitNumber }
      : emptyUserLotForm();
    setUserLotForm(applyOfficialBuiltSurfaceToForm(base, officialUnit));
    setUfWizardStep(UF_WIZARD_STEP.BUILDING_TYPE);
    setUfAttributeSchema(null);
  }, []);

  const handleUserLotFormChange = useCallback((updater) => {
    setUserLotForm((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      return applyOfficialBuiltSurfaceToForm(
        next,
        pendingOfficialUnit || getOfficialUnitFromUserLot(userLotData),
      );
    });
  }, [pendingOfficialUnit, userLotData]);

  // Navega desde la lista al detalle al seleccionar una unidad
  const handleSelectUnitFromList = useCallback(async (unitId) => {
    ufBuildingTypeDraftPendingRef.current = false;
    ufCreatedInWizardRef.current = false;
    setHighlightUnitId(null);
    setUfListingBootstrapLoading(false);
    const detail = await handleSelectUnit(unitId);
    setUnitView('detail');
    return detail;
  }, [handleSelectUnit]);

  const initialUserLotAppliedRef = useRef(null);
  const sharedUnitAutoSelectedRef = useRef(null);

  useEffect(() => {
    initialUserLotAppliedRef.current = null;
    sharedUnitAutoSelectedRef.current = null;
  }, [polygonId]);

  // Navega desde la lista al formulario vacío para crear unidad
  const handleAddUnitFromList = useCallback(() => {
    handleAddUnit(null);
    setUnitView('detail');
  }, [handleAddUnit]);

  const handleAnnotateOfficialUnit = useCallback((officialUnit) => {
    handleAddUnit(officialUnit);
    setUnitView('detail');
  }, [handleAddUnit]);

  const handleEditOfficialOverlay = useCallback(async (userLotId) => {
    const detail = await handleSelectUnitFromList(userLotId);
    if (!detail) return;
    setIsEditing(true);
    setUfWizardStep(resolveInitialWizardStep({
      isEditing: true,
      buildingType: detail.building_type,
    }));
    if (normalizeBuildingType(detail.building_type)) {
      loadUfSchema(detail.building_type);
    }
  }, [handleSelectUnitFromList, loadUfSchema]);

  const handleBackToUnitList = useCallback(async () => {
    const { cancelled } = await confirmDiscardUfBuildingTypeDraftIfNeeded();
    if (cancelled) return;
    setUnitView('list');
    setIsEditing(false);
    setIsNewUnit(false);
    setSelectedUnitId(null);
    setPendingOfficialUnit(null);
    setUfWizardStep(UF_WIZARD_STEP.BUILDING_TYPE);
    setUfAttributeSchema(null);
  }, [confirmDiscardUfBuildingTypeDraftIfNeeded]);

  const ownsUserLot = Boolean(userLotData && currentUserId && userLotData.owner_id === currentUserId);
  const ownerCanManage = ownsUserLot && !isSpaceGuest;
  // Espacio ajeno (mig 082): permisos por UF desde la API (`can_edit_all` /
  // `can_delete`, true solo si la creó el invitado). Sin flags → regla acotada F2.
  const spaceGuestAccess = isSpaceGuest && userLotData?.id
    ? resolveSpaceGuestUnitAccess(userLotData)
    : { canEditAll: false, canDelete: false };
  // `isOwner` gatea Eliminar UF: dueño, o invitado con `can_delete`.
  const isOwner = ownerCanManage || spaceGuestAccess.canDelete;
  // Alta de UF: también el invitado (UF del owner con `owner_user_id`).
  const canCreate = !readOnly && userLotChecked ? !userLotData && !!currentUserId : false;
  const canEdit = ownerCanManage || spaceGuestAccess.canEditAll || canCreate;
  // F1: en espacio ajeno el invitado ve las UF del owner en solo lectura.
  const canView = readOnly ? true : (canEdit || ownsUserLot || Boolean(spaceReadOwnerUserId));
  const isPublicWithoutLogin = !readOnly && !currentUserId;
  const isAnonymousSharedView = readOnly && !currentUserId && (publicLotView || sharedToken || sharedUserLotToken);
  const shouldMaskSensitive = !currentUserId && (isPublicWithoutLogin || isAnonymousSharedView);
  const showSharedListUnitsTab = isSharedListView
    ? normalizedSharedListUnits.length > 0
    : units.length > 0;
  const showSharedUserLotUnitsTab = userLotChecked && !loadingUserLot && units.length > 0;
  const showDetailsTab = !(readOnly && publicLotView)
    && !isPublicWithoutLogin
    && !(readOnly && sharedUserLotToken && !showSharedUserLotUnitsTab)
    && !(isSharedListView && !showSharedListUnitsTab);
  const buildingVisualizationRequiresLogin = !currentUserId || lotBuildingStatus === 'login_required';
  const hasOfficialList = officialUnits.length > 0;
  const showUnitList = hasOfficialList || units.length >= 1;
  const waitingOfficialUnits = fetchOfficialUnits && officialUnitsLoading;
  const showUnitsListSkeleton = unitView === 'list' && (loadingUserLot || waitingOfficialUnits);
  const showUnitsListPane = unitView === 'list' && (showUnitList || showUnitsListSkeleton);
  const showUnitsDetailPane = unitView === 'detail'
    || (!showUnitList && !showUnitsListSkeleton && !sharedUserLotToken);
  const detailOfficialUnit = getOfficialUnitFromUserLot(userLotData) || pendingOfficialUnit;

  // Destino de la consulta (lista y lote compartidos, misma regla, 1 o N UFs):
  // pestaña Lote → el lote; Unidades Funcionales con una UF abierta → esa UF;
  // Unidades Funcionales en el listado (sin UF abierta) → sin form ni “Consultar”.
  // No se usa la UF auto-seleccionada / de la URL fuera de su detalle.
  const {
    allowed: inquiryContextAllowed,
    unitOpen: inquiryUnitOpen,
  } = resolveInquiryPanelContext({ activeTab, showDetailsTab, unitView, showUnitsDetailPane });
  // Consulta lista compartida: solo con UF abierta o lote ítem de la lista (nunca lot_id padre suelto).
  const openSharedInquiryUserLotId = inquiryUnitOpen
    ? String(selectedUnitId || sharedUserLotId || '').trim()
    : '';
  const sharedInquiryUnitLabel = useMemo(() => {
    const fromForm = String(userLotForm?.title || '').trim();
    if (fromForm) return fromForm;
    const fromData = String(userLotData?.title || '').trim();
    if (fromData) return fromData;
    const match = normalizedSharedListUnits.find((unit) => (
      String(unit.id) === openSharedInquiryUserLotId
      || String(unit.user_lot_id) === openSharedInquiryUserLotId
    ));
    return match ? getUnitPublicTitle(match) : '';
  }, [
    userLotForm?.title,
    userLotData?.title,
    normalizedSharedListUnits,
    openSharedInquiryUserLotId,
  ]);
  const sharedInquiryLotLabel = useMemo(() => {
    const fromContent = resolveLotContentTitle(sharedLotUserContent)
      || resolveLotContentTitle(sharedListLotUserContent);
    if (fromContent) return fromContent;
    return String(editedInfo?.name || polygonName || '').trim();
  }, [sharedLotUserContent, sharedListLotUserContent, editedInfo?.name, polygonName]);
  // Lista: el lote se consulta si es ítem directo o si la lista expone su
  // contenido privado (`lot_user_content` != null en getsharedlist, API 68b290a).
  const sharedListLotInquirable = Boolean(sharedListIncludesLot || sharedListLotUserContent != null);
  const sharedListInquiryTarget = useMemo(() => resolveSharedListInquiryTarget({
    openUserLotId: openSharedInquiryUserLotId,
    lotId: editedInfo?.id || polygonId || '',
    lotInList: sharedListLotInquirable,
    unitLabel: sharedInquiryUnitLabel,
    lotLabel: sharedInquiryLotLabel,
  }), [
    openSharedInquiryUserLotId,
    editedInfo?.id,
    polygonId,
    sharedListLotInquirable,
    sharedInquiryUnitLabel,
    sharedInquiryLotLabel,
  ]);
  const showSharedListInquiry = Boolean(
    isSharedListView && sharedToken && !collectionCanEdit && inquiryContextAllowed
      && sharedListInquiryTarget.canShow,
  );
  // F3: lote / UF compartido solo. En la pestaña Lote se consulta por el lote;
  // con una UF del share abierta, por esa UF; en el listado de UFs, sin form. Mismo form, login y vuelta que listas.
  const openSharedLotUnitId = readOnly && sharedUserLotToken && inquiryUnitOpen
    ? String(selectedUnitId || '').trim()
    : '';
  const sharedLotInquiryUnitLabel = useMemo(() => {
    if (!openSharedLotUnitId) return '';
    const fromForm = String(userLotForm?.title || userLotData?.title || '').trim();
    if (fromForm) return fromForm;
    const match = units.find((unit) => (
      String(unit?.id) === openSharedLotUnitId || String(unit?.user_lot_id) === openSharedLotUnitId
    ));
    return match ? getUnitPublicTitle(match) : '';
  }, [openSharedLotUnitId, userLotForm?.title, userLotData?.title, units]);
  const sharedLotInquiryTarget = useMemo(() => resolveSharedLotInquiryTarget({
    openUserLotId: openSharedLotUnitId,
    shareUnits: units,
    unitLabel: sharedLotInquiryUnitLabel,
    lotLabel: sharedInquiryLotLabel,
  }), [openSharedLotUnitId, units, sharedLotInquiryUnitLabel, sharedInquiryLotLabel]);
  const showSharedLotInquiry = Boolean(
    readOnly && sharedUserLotToken && userLotChecked && editedInfo?.id && inquiryContextAllowed,
  );
  // Form de consulta en modo cliente: `can_edit` in-app o `viewer_can_manage` del share (SQL 099).
  const inquiryManage = Boolean(collectionCanEdit || viewerCanManage);
  const inquiryFormProps = showSharedListInquiry
    ? {
      kind: 'list',
      shareToken: sharedToken,
      lotId: sharedListInquiryTarget.lotId,
      userLotId: sharedListInquiryTarget.userLotId,
      parcelLabel: sharedListInquiryTarget.parcelLabel,
      loginRedirectPath: `/shared/${encodeURIComponent(sharedToken)}`,
    }
    : showSharedLotInquiry
      ? {
        kind: 'lot',
        shareToken: sharedUserLotToken,
        userLotId: sharedLotInquiryTarget.userLotId,
        parcelLabel: sharedLotInquiryTarget.parcelLabel,
        loginRedirectPath: `/shared-user-lot/${encodeURIComponent(sharedUserLotToken)}`,
      }
      : null;

  useEffect(() => {
    if (!showDetailsTab && activeTab === 1) {
      setActiveTab(0);
    }
  }, [showDetailsTab, activeTab]);

  useEffect(() => {
    if (!initialOpenLotContent) return;
    setActiveTab(0);
  }, [initialOpenLotContent, polygonInfo?.id]);

  useEffect(() => {
    if (!initialUserLotId || !editedInfo?.id || !userLotChecked || !units.length) return;

    const applyKey = `${editedInfo.id}:${initialUserLotId}`;
    if (initialUserLotAppliedRef.current === applyKey) return;

    const match = units.find(
      (unit) => String(unit.id) === String(initialUserLotId)
        || String(unit.user_lot_id) === String(initialUserLotId),
    );
    const unitId = match?.id || match?.user_lot_id;
    if (!unitId) return;

    initialUserLotAppliedRef.current = applyKey;
    let cancelled = false;

    if (showDetailsTab) {
      setActiveTab(1);
    }

    (async () => {
      await handleSelectUnitFromList(unitId);
      if (cancelled) return;
      // Volver desde un lead (`initialUserLotEdit=false`): abre la UF sin editar.
      if (showDetailsTab && initialUserLotEdit) {
        setIsEditing(true);
      }
      onEditModeConsumed?.();
      onInitialUserLotIdConsumed?.();
    })();

    return () => {
      cancelled = true;
    };
  }, [
    initialUserLotId,
    editedInfo?.id,
    units,
    userLotChecked,
    handleSelectUnitFromList,
    showDetailsTab,
    initialUserLotEdit,
    onEditModeConsumed,
    onInitialUserLotIdConsumed,
  ]);

  // ── Styles ──────────────────────────────────────────────────────────────────
  const dockTextFieldSx = {
    '& .MuiInputLabel-root': {
      color: colors.textPrimary,
      opacity: 0.85,
      fontFamily: typography.fontFamily.primary,
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: '#ffffff',
      opacity: 0.95,
    },
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'rgba(0,0,0,0.20)',
      borderRadius: 2,
      '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
      '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.20)' },
      '&.Mui-focused fieldset': { borderColor: 'rgba(255,255,255,0.28)' },
    },
    '& .MuiOutlinedInput-input': {
      color: '#ffffff',
      fontFamily: typography.fontFamily.primary,
    },
    '& .MuiInputBase-input.Mui-disabled': {
      WebkitTextFillColor: 'rgba(255,255,255,0.82)',
    },
    '& .MuiInputLabel-root.Mui-disabled': {
      color: 'rgba(255,255,255,0.72)',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255,255,255,0.12)',
    },
  };

  const dockSecondaryTextSx = {
    color: colors.textSecondary,
    fontFamily: typography.fontFamily.primary,
  };

  const fieldSx = {
    ...dockTextFieldSx,
    '& .MuiOutlinedInput-input': {
      ...dockTextFieldSx['& .MuiOutlinedInput-input'],
      WebkitTextFillColor: isEditing ? '#ffffff' : 'rgba(255,255,255,0.82)',
    },
  };

  // ── Effects ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    // Si había un borrador UF solo con tipo, limpiarlo al cambiar de lote.
    const pendingDraftId = ufBuildingTypeDraftPendingRef.current
      ? userLotDataRef.current?.id
      : null;
    const pendingDraftOwnerUserId = ufDraftOwnerRef.current;
    ufBuildingTypeDraftPendingRef.current = false;
    ufDraftOwnerRef.current = null;
    ufCreatedInWizardRef.current = false;
    setHighlightUnitId(null);
    setUfListingBootstrapLoading(false);
    if (pendingDraftId) {
      deleteUserLot(
        pendingDraftId,
        resolveUfDraftDiscard({ draftOwnerUserId: pendingDraftOwnerUserId }).deleteOptions,
      ).catch((e) => {
        console.error('Error discarding UF draft on lot change:', e);
      });
    }

    setEditedInfo(
      polygonId
        ? {
            id: polygonId,
            name: polygonName,
            province: polygonProvince,
            department: polygonDepartment,
            valuation: polygonValuation,
            urbansurface: polygonUrbanSurface,
            surface: polygonSurface,
            built_footprint_m2: polygonBuiltFootprintM2,
            max_footprint_m2: polygonMaxFootprintM2,
            max_buildable_m2: polygonMaxBuildableM2,
            neighborhood: polygonNeighborhood,
            locality: polygonLocality,
            municipality: polygonMunicipality,
            street: polygonStreet,
            state: polygonState,
            type: polygonType,
            lat: polygonLat,
            lng: polygonLng,
            quota: polygonQuota,
            zoning_zone: polygonZoningZone,
            zoning_perfil: polygonZoningPerfil,
            flood_risk_pct: polygonFloodRiskPct,
            chronic_poverty_pct: polygonChronicPovertyPct,
            population_density: polygonPopulationDensity,
            front_highway_type: polygonFrontHighwayType,
            street_position: polygonStreetPosition,
            neighborhood_type: polygonNeighborhoodType,
            lot_shape: polygonLotShape,
            front_azimuth: polygonFrontAzimuth,
            water_network_deficit_pct: polygonWaterNetworkDeficitPct,
            sewer_deficit_pct: polygonSewerDeficitPct,
            cooking_fuel_deficit_pct: polygonCookingFuelDeficitPct,
            boundary: polygonBoundary,
          }
        : null
    );
    if (sharedUserLotId || initialUserLotId) {
      if (!readOnly || !sharedUserLotToken) {
        // En vista compartida por token, el tab de UFs se abre cuando el snapshot termina de cargar.
        setActiveTab(1);
      }
    } else if (isSharedListView && sharedOpenUnitsList) {
      // Mapa N>1 UFs: tab Unidades en lista, sin detalle de una UF.
      setActiveTab(1);
    } else {
      setActiveTab(0);
    }
    setUserLotData(undefined);
    setUserLotChecked(false);
    setLoadingUserLot((readOnly ? true : !!currentUserId) && !!polygonId);
    setIsEditing(false);
    setUserLotForm(emptyUserLotForm());
    setUserLotLists([]);
    setSharedLotUserContent(null);
    if (isSharedListView) {
      setUnits(normalizedSharedListUnitsRef.current);
      if (!sharedUserLotId) {
        setUnitView('list');
        setSelectedUnitId(null);
      }
    } else {
      setUnits([]);
      setUnitView('list');
      setSelectedUnitId(null);
    }
    setIsNewUnit(false);
    setPendingOfficialUnit(null);
  }, [
    polygonId,
    polygonName,
    polygonProvince,
    polygonDepartment,
    polygonValuation,
    polygonUrbanSurface,
    polygonSurface,
    polygonBuiltFootprintM2,
    polygonMaxFootprintM2,
    polygonMaxBuildableM2,
    polygonNeighborhood,
    polygonLocality,
    polygonMunicipality,
    polygonStreet,
    polygonState,
    polygonType,
    polygonLat,
    polygonLng,
    polygonQuota,
    polygonZoningZone,
    polygonZoningPerfil,
    polygonFloodRiskPct,
    polygonChronicPovertyPct,
    polygonPopulationDensity,
    polygonFrontHighwayType,
    polygonStreetPosition,
    polygonNeighborhoodType,
    polygonLotShape,
    polygonFrontAzimuth,
    polygonWaterNetworkDeficitPct,
    polygonSewerDeficitPct,
    polygonCookingFuelDeficitPct,
    polygonBoundary,
    currentUserId,
    readOnly,
    publicLotView,
    initialUserLotId,
    sharedUserLotToken,
    sharedUserLotId,
    sharedOpenUnitsList,
    isSharedListView,
  ]);

  useEffect(() => {
    if (!isSharedListView) return undefined;
    setUnits(normalizedSharedListUnits);
    return undefined;
  }, [isSharedListView, normalizedSharedListUnits]);

  useEffect(() => {
    if (!isSharedListView || sharedUserLotId) return undefined;
    if (sharedOpenUnitsList) {
      setActiveTab(1);
    } else {
      setActiveTab(0);
    }
    setUnitView('list');
    setSelectedUnitId(null);
    sharedUnitAutoSelectedRef.current = null;
    return undefined;
  }, [isSharedListView, sharedUserLotId, sharedOpenUnitsList, polygonId]);

  useEffect(() => {
    if (!restrictToListId || initialUserLotId || sharedUserLotId) return undefined;
    setActiveTab(0);
    setUnitView('list');
    setSelectedUnitId(null);
    setUserLotData(null);
    initialUserLotAppliedRef.current = null;
    return undefined;
  }, [restrictToListId, initialUserLotId, sharedUserLotId, polygonId]);

  useEffect(() => {
    if (!isSharedListView || !sharedUserLotId) return undefined;
    const autoSelectKey = `${polygonId}:${sharedUserLotId}`;
    if (sharedUnitAutoSelectedRef.current === autoSelectKey) return undefined;

    const listUnits = normalizedSharedListUnits.length > 0 ? normalizedSharedListUnits : units;
    if (!listUnits.length) return undefined;

    const match = listUnits.find(
      (unit) => String(unit.id) === String(sharedUserLotId)
        || String(unit.user_lot_id) === String(sharedUserLotId),
    );
    if (!match) return undefined;

    const unitId = match.id || match.user_lot_id;
    sharedUnitAutoSelectedRef.current = autoSelectKey;

    setActiveTab(1);
    setUnitView('detail');
    setSelectedUnitId(unitId);
    setIsNewUnit(false);
    setIsEditing(false);
    return undefined;
  }, [
    isSharedListView,
    sharedUserLotId,
    polygonId,
    normalizedSharedListUnits,
    units,
  ]);

  useEffect(() => {
    if (!isSharedListView || !sharedUserLotId || lotDetailLoading || !sharedLotDetail) return undefined;
    const detailId = sharedLotDetail.user_lot_id ?? sharedLotDetail.id;
    if (detailId != null && String(detailId) !== String(sharedUserLotId)) return undefined;
    let cancelled = false;
    setUserLotData(sharedLotDetail);
    (async () => {
      await hydrateSharedUnitForm(sharedLotDetail);
      if (cancelled) return;
    })();
    return () => {
      cancelled = true;
    };
  }, [
    isSharedListView,
    sharedUserLotId,
    lotDetailLoading,
    sharedLotDetail,
    hydrateSharedUnitForm,
  ]);

  useEffect(() => {
    if (initialUserLotId) return;
    if (polygonInfo?.id && openInEditMode) {
      if (showDetailsTab) {
        setActiveTab(1);
        setIsEditing(true);
      } else {
        setActiveTab(0);
        setIsEditing(false);
      }
      onEditModeConsumed?.();
    }
  }, [polygonInfo?.id, openInEditMode, onEditModeConsumed, showDetailsTab, initialUserLotId]);

  useEffect(() => {
    if (!editedInfo?.id) return;

    if (readOnly) {
      if (publicLotView) {
        setUserLotData(null);
        setUserLotChecked(true);
        setLoadingUserLot(false);
        return;
      }

      if (sharedUserLotToken) {
        let cancelled = false;
        const fetchSharedUserLot = async () => {
          setLoadingUserLot(true);
          try {
            const res = await getSharedUserLot(sharedUserLotToken);
            if (cancelled) return;
            if (res?.data) {
              const data = res.data;
              const sharedUnits = Array.isArray(data.units)
                ? data.units.map((unit) => ({
                    ...unit,
                    id: unit.id || unit.user_lot_id,
                    user_lot_id: unit.user_lot_id || unit.id,
                  }))
                : [];

              setUnits(sharedUnits);
              setSelectedUnitId(null);
              setIsNewUnit(false);
              setUnitView(sharedUnits.length >= 1 ? 'list' : 'list');
              setUserLotData(null);
              setUserLotForm(emptyUserLotForm());

              // Si el share es solo parcela, no hay unidades que listar.
              if (data.lot_only === true || sharedUnits.length === 0) {
                setUserLotData(null);
              }

              setSharedLotUserContent(
                data.lot_user_content != null ? data.lot_user_content : null,
              );
            } else {
              setUnits([]);
              setUserLotData(null);
              setSharedLotUserContent(null);
            }
          } catch (e) {
            if (cancelled) return;
            console.error('Error fetching shared user lot:', e);
            setUnits([]);
            setUserLotData(null);
            setSharedLotUserContent(null);
          } finally {
            if (cancelled) return;
            setLoadingUserLot(false);
            setUserLotChecked(true);
          }
        };
        fetchSharedUserLot();
        return () => {
          cancelled = true;
        };
      }

      if (skipSharedLotDetailFetch) {
        if (sharedLotDetail) {
          setEditedInfo((prev) => mergeSharedLotIntoEditedInfo(prev, sharedLotDetail));
          if (sharedUserLotId) {
            setUserLotData(sharedLotDetail);
            void hydrateSharedUnitForm(sharedLotDetail);
          } else {
            setUserLotData(null);
          }
        } else {
          setUserLotData(null);
        }
        setLoadingUserLot(lotDetailLoading);
        setUserLotChecked(true);
        return undefined;
      }

      if (!sharedToken || (!sharedUserLotId && !sharedLotDetail)) {
        setUserLotData(null);
        setUserLotChecked(true);
        setLoadingUserLot(false);
        return undefined;
      }

      if (!sharedUserLotId) {
        return undefined;
      }

      let cancelled = false;
      const fetchShared = async () => {
        setLoadingUserLot(true);
        try {
          const res = await getSharedLotDetail(sharedToken, { userLotId: sharedUserLotId });
          if (cancelled) return;
          if (res?.data) {
            setUserLotData(res.data);
            setEditedInfo((prev) => mergeSharedLotIntoEditedInfo(prev, res.data));
            setSharedLotUserContent(
              res.data.lot_user_content != null ? res.data.lot_user_content : null,
            );
            await hydrateSharedUnitForm(res.data);
          } else {
            setUserLotData(null);
            setSharedLotUserContent(null);
          }
        } catch (e) {
          if (cancelled) return;
          console.error('Error fetching shared lot detail:', e);
          setUserLotData(null);
          setSharedLotUserContent(null);
        } finally {
          if (cancelled) return;
          setLoadingUserLot(false);
          setUserLotChecked(true);
        }
      };
      fetchShared();
      return () => {
        cancelled = true;
      };
    }

    // Si no hay usuario autenticado, no tiene sentido intentar cargar info.
    if (!currentUserId) {
      setUserLotData(null);
      setUserLotChecked(true);
      setLoadingUserLot(false);
      return;
    }

    let cancelled = false;

    const fetchUserLotData = async () => {
      setLoadingUserLot(true);
      try {
        // Cargar todas las unidades del lote para mostrar el selector
        // (en espacio ajeno, las del owner: F1).
        const unitsRes = await getUserLotUnits(editedInfo.id, {
          ownerUserId: spaceReadOwnerUserId,
          includeCommercialSummary: canLoadCommercial,
        });
        if (cancelled) return;
        setUnitsCommercial(canLoadCommercial ? extractUnitsCommercial(unitsRes) : null);

        const allUnits = Array.isArray(unitsRes?.data) ? unitsRes.data : [];
        setUnits(filterUnitsByListId(allUnits, restrictToListId));

        // Siempre mostrar la lista primero; el detalle se carga al elegir una unidad
        setSelectedUnitId(null);
        setUserLotData(null);
        setUnitView('list');
      } catch (e) {
        if (cancelled) return;
        console.error('Error fetching user lot data:', e);
        setUserLotData(null);
      } finally {
        if (cancelled) return;
        setLoadingUserLot(false);
        setUserLotChecked(true);
      }
    };

    fetchUserLotData();

    return () => {
      cancelled = true;
    };
  }, [editedInfo?.id, currentUserId, readOnly, sharedToken, sharedUserLotId, sharedUserLotToken, publicLotView, restrictToListId, skipSharedLotDetailFetch, sharedLotDetail, lotDetailLoading, hydrateSharedUnitForm, spaceReadOwnerUserId, canLoadCommercial]);

  // Resumen comercial: limpiar al cambiar de lote / espacio / vista.
  useEffect(() => {
    setUnitsCommercial(null);
    setUfCommercial(null);
  }, [editedInfo?.id, spaceReadOwnerUserId, canLoadCommercial]);

  // Tras cambiar una etapa / marcar consultas vistas: volver a pedir solo los
  // bloques comerciales (no se recalcula en el cliente ni se pisan UF / form).
  const selectedUnitIdForCommercialRef = useRef(null);
  selectedUnitIdForCommercialRef.current = selectedUnitId;
  useEffect(() => {
    const lotId = editedInfo?.id;
    if (!canLoadCommercial || !lotId) return undefined;
    let timer = null;
    let alive = true;
    const ownerUserId = spaceReadOwnerUserId;
    const refresh = async () => {
      try {
        const unitsRes = await getUserLotUnits(lotId, { ownerUserId, includeCommercialSummary: true });
        if (alive) setUnitsCommercial(extractUnitsCommercial(unitsRes));
      } catch (_) { /* se mantiene el último */ }
      const unitId = selectedUnitIdForCommercialRef.current;
      if (!unitId) return;
      try {
        const res = await getUserLotDetail(lotId, unitId, { ownerUserId, includeCommercialSummary: true });
        if (alive && String(selectedUnitIdForCommercialRef.current) === String(unitId)) {
          setUfCommercial(res?.data?.commercial_summary
            ? { id: String(unitId), summary: res.data.commercial_summary }
            : null);
        }
      } catch (_) { /* se mantiene el último */ }
    };
    const off = onCommercialChanged(() => {
      clearTimeout(timer);
      timer = setTimeout(refresh, 400);
    });
    return () => {
      alive = false;
      clearTimeout(timer);
      off();
    };
  }, [editedInfo?.id, spaceReadOwnerUserId, canLoadCommercial]);

  const unitsForList = useMemo(
    () => attachUnitsCommercial(units, unitsCommercial),
    [units, unitsCommercial],
  );
  // UF abierta: el del detalle; mientras carga, el de la fila (placeholder).
  const selectedUfCommercial = useMemo(() => {
    if (!canLoadCommercial || !selectedUnitId) return null;
    if (ufCommercial && ufCommercial.id === String(selectedUnitId)) return ufCommercial.summary;
    return unitsCommercial?.units?.[String(selectedUnitId)] || null;
  }, [canLoadCommercial, selectedUnitId, ufCommercial, unitsCommercial]);

  // Re-aplica datos de getsharedlotdetail tras actualizar polygonInfo (p. ej. boundary catastral).
  useEffect(() => {
    if (!readOnly || !skipSharedLotDetailFetch || !sharedLotDetail) return undefined;
    // Defensa: nunca aplicar el detalle de otra UF (el padre ya filtra por selección).
    const sharedDetailId = sharedLotDetail.user_lot_id ?? sharedLotDetail.id;
    if (sharedUserLotId && sharedDetailId != null && String(sharedDetailId) !== String(sharedUserLotId)) {
      return undefined;
    }
    let cancelled = false;
    setUserLotData(sharedLotDetail);
    setEditedInfo((prev) => mergeSharedLotIntoEditedInfo(prev, sharedLotDetail));
    setSharedLotUserContent(
      sharedLotDetail.lot_user_content != null ? sharedLotDetail.lot_user_content : null,
    );
    void hydrateSharedUnitForm(sharedLotDetail).finally(() => {
      if (cancelled) return;
      setLoadingUserLot(false);
      setUserLotChecked(true);
    });
    return () => {
      cancelled = true;
    };
  }, [
    readOnly,
    skipSharedLotDetailFetch,
    sharedLotDetail,
    sharedUserLotId,
    hydrateSharedUnitForm,
    polygonBoundary,
    polygonUrbanSurface,
    polygonChronicPovertyPct,
    polygonPopulationDensity,
    polygonFrontHighwayType,
    polygonStreetPosition,
    polygonNeighborhoodType,
    polygonLotShape,
    polygonFrontAzimuth,
  ]);

  useEffect(() => {
    if (readOnly || !currentUserId || !editedInfo?.id) return;
    refreshUserLotLists();
  }, [currentUserId, editedInfo?.id, refreshUserLotLists, readOnly]);

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const handleTabChange = useCallback(async (_, newValue) => {
    if (newValue === activeTab) return;

    // Salir de Unidades Funcionales con borrador solo-tipo → confirmar pérdida de datos.
    const leavingUfTab = showDetailsTab && activeTab === 1 && newValue !== 1;
    if (leavingUfTab) {
      const { discarded, cancelled } = await confirmDiscardUfBuildingTypeDraftIfNeeded();
      if (cancelled) return;
      if (discarded) {
        setIsEditing(false);
        setUnitView('list');
        setUfWizardStep(UF_WIZARD_STEP.BUILDING_TYPE);
        setUfListingBootstrapLoading(false);
      }
    }

    setActiveTab(newValue);
  }, [activeTab, showDetailsTab, confirmDiscardUfBuildingTypeDraftIfNeeded]);

  const handleMenuOpen = (e) => setMenuAnchor(e.currentTarget);
  const handleMenuClose = () => setMenuAnchor(null);

  const openShareLotDialog = () => {
    // Evita aria-hidden sobre el trigger del menú que aún retiene foco.
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    window.setTimeout(() => {
      setShareLotDialogOpen(true);
    }, 0);
  };

  // Leads (R1): "Consultas y leads" en espacio propio o invitado de Espacio;
  // shares y editor solo de lista llegan con `readOnly`. V3-0: sin "Agregar a
  // lead" (los intereses nacen de una consulta del form). Ver docs/maps/leads.md.
  const canViewRecordLeads = Boolean(currentUserId) && !readOnly;

  // R1: "Consultas y leads" de la UF guardada (sin shares / visitantes /
  // `readOnly`); 403 / 404 la ocultan sola.
  // Abrir un lead desde la sección: pasa la UF para que el volver del lead la reabra.
  const handleOpenLeadFromUnit = (leadId) => {
    onOpenLead?.(leadId, {
      lotId: editedInfo?.id,
      userLotId: userLotData?.id,
      label: getUnitHeaderTitle(userLotData, null),
    });
  };
  const recordLeadsNode = canViewRecordLeads && userLotData?.id ? (
    <RecordLeadsSection
      key={`record-leads:${spaceReadOwnerUserId || 'own-space'}:${userLotData.id}`}
      itemKind="user_lot"
      recordId={userLotData.id}
      ownerUserId={spaceReadOwnerUserId}
      onOpenLead={onOpenLead ? handleOpenLeadFromUnit : undefined}
      lastActivityAt={selectedUfCommercial?.last_activity_at || null}
    />
  ) : null;

  // Meta Ads E4: «Campañas» con las mismas condiciones que «Consultas y leads» (se oculta sola
  // sin flag / sin provider de MapPage / 403 / 404). Ver docs/maps/meta-ads.md.
  const recordCampaignsNode = canViewRecordLeads && userLotData?.id ? (
    <RecordCampaignsSection
      key={`record-campaigns:${spaceReadOwnerUserId || 'own-space'}:${userLotData.id}`}
      itemKind="user_lot"
      recordId={userLotData.id}
      ownerUserId={spaceReadOwnerUserId}
    />
  ) : null;

  // Comparación de mercado (T1): «Precio vs. mercado» debajo del precio, mismas condiciones
  // que Leads / Campañas. Render prop: la pestaña pone `onEdit` (su «Editar») y `revision`.
  // Se esconde sola sin flag / `features.market_comparison` / 401 / 403 / 404.
  // Ver docs/maps/market-comparison.md.
  const userLotMarketRevision = marketRevisionOf(userLotData);
  // Modo Comparables (T3): centro del círculo = punto de la unidad, o del lote.
  const lotMarketCenter = normalizeMarketCenter(getLotRepresentativeLatLng(editedInfo))
    || geometryCenter(polygonInfo?.geometry);
  const unitMarketCenter = normalizeMarketCenter(getLotRepresentativeLatLng(userLotData)) || lotMarketCenter;
  const showMarketComparables = onShowMarketComparables
    ? (view) => onShowMarketComparables({ ...view, source: 'dock', anchorId: polygonId != null ? String(polygonId) : null })
    : undefined;
  const renderUnitMarketComparison = canViewRecordLeads && userLotData?.id ? ({ onEdit } = {}) => (
    <MarketComparisonCard
      key={`market:${spaceReadOwnerUserId || 'own-space'}:${userLotData.id}`}
      itemKind="user_lot"
      recordId={userLotData.id}
      ownerUserId={spaceReadOwnerUserId}
      revision={userLotMarketRevision}
      unitLabel={getUnitHeaderTitle(userLotData, null)}
      onEdit={onEdit}
      center={unitMarketCenter}
      onShowOnMap={showMarketComparables}
    />
  ) : null;

  // R1 fase 2: mismo bloque en Lote (`cadastral_lot`), montado dentro de
  // "Información privada" solo si hay registro privado (`LotInfoTab`). Consultas /
  // leads solo del lote (`lot_id` sin UF); las UFs muestran las suyas en su ficha.
  const lotRecordLabel = editedInfo?.name ? `Lote ${editedInfo.name}` : 'el lote';
  const handleOpenLeadFromLot = (leadId) => {
    onOpenLead?.(leadId, { lotId: editedInfo?.id, userLotId: null, label: lotRecordLabel });
  };
  const lotRecordLeadsNode = canViewRecordLeads && editedInfo?.id && !publicLotView ? (
    <RecordLeadsSection
      key={`record-leads-lot:${spaceReadOwnerUserId || 'own-space'}:${editedInfo.id}`}
      itemKind="cadastral_lot"
      recordId={editedInfo.id}
      ownerUserId={spaceReadOwnerUserId}
      onOpenLead={onOpenLead ? handleOpenLeadFromLot : undefined}
      lastActivityAt={canLoadCommercial ? unitsCommercial?.lot?.last_activity_at || null : null}
    />
  ) : null;

  const lotRecordCampaignsNode = canViewRecordLeads && editedInfo?.id && !publicLotView ? (
    <RecordCampaignsSection
      key={`record-campaigns-lot:${spaceReadOwnerUserId || 'own-space'}:${editedInfo.id}`}
      itemKind="cadastral_lot"
      recordId={editedInfo.id}
      ownerUserId={spaceReadOwnerUserId}
    />
  ) : null;

  const renderLotMarketComparison = canViewRecordLeads && editedInfo?.id && !publicLotView
    ? ({ onEdit, revision } = {}) => (
      <MarketComparisonCard
        key={`market-lot:${spaceReadOwnerUserId || 'own-space'}:${editedInfo.id}`}
        itemKind="cadastral_lot"
        recordId={editedInfo.id}
        ownerUserId={spaceReadOwnerUserId}
        revision={revision}
        unitLabel={lotRecordLabel === 'el lote' ? 'Lote' : lotRecordLabel}
        onEdit={onEdit}
        center={lotMarketCenter}
        onShowOnMap={showMarketComparables}
      />
    ) : null;

  const handleShare = async () => {
    handleMenuClose();
    // Espacio ajeno (F3): el diálogo comparte el lote del owner (`spaceWriteOwnerUserId`).
    const userLotId = userLotData?.id;
    setSharePreselectedUnitIds(userLotId ? [String(userLotId)] : []);

    if (userLotId || units.length > 0 || !userLotChecked || loadingUserLot) {
      openShareLotDialog();
      return;
    }

    const lotIdForShare = editedInfo?.id;
    if (lotIdForShare) {
      try {
        const res = spaceWriteOwnerUserId
          ? await getLotUserContent(lotIdForShare, { ownerUserId: spaceWriteOwnerUserId })
          : await getLotUserContent(lotIdForShare);
        if (res._httpOk && buildLotUserContentSharePreview(res.data).length > 0) {
          openShareLotDialog();
          return;
        }
      } catch {
        // Si falla la consulta, se usa el enlace público de Terrio.
      }
    }

    const url = buildPublicLotUrl(editedInfo?.id);
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const el = document.createElement("textarea");
      el.value = url;
      el.style.position = "fixed";
      el.style.opacity = "0";
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    }
    toast.success('¡Link copiado!');
  };

  const handleToggleFavoriteWithFeedback = () => {
    handleMenuClose();
    const wasAdded = !isFavorite;
    onToggleFavorite && onToggleFavorite(editedInfo.id);
    if (wasAdded) {
      toast.success('Marcador agregado');
    } else {
      toast.info('Marcador quitado');
    }
  };

  const handleReportParcelIssue = () => {
    handleMenuClose();
    setParcelFeedbackOpen(true);
  };

  const handleSaveUserLot = async ({
    schemaOverride = null,
    partialStep = null,
    silent = false,
    /** Botón «Guardar» del form (alta o edición): al salir bien vuelve al listado de UF. */
    finalSave = false,
  } = {}) => {
    if (!editedInfo?.id) return false;
    // Espacio ajeno: el invitado guarda en el espacio del owner. Alta y UF que
    // creó él (`can_edit_all`) → mismo payload que el dueño (sin `is_public`);
    // otra UF existente → payload acotado (copy comercial + `stock_status`).
    const writeOwnerUserId = spaceWriteOwnerUserId;
    const guestCoreUpdate = Boolean(
      writeOwnerUserId && userLotData?.id && !spaceGuestAccess.canEditAll,
    );
    const isStaleSpace = () => spaceWriteOwnerRef.current !== writeOwnerUserId;
    setIsSaving(true);
    try {
      const normalizedListIds = Array.isArray(userLotForm.list_ids)
        ? userLotForm.list_ids.filter(Boolean)
        : [];
      const schemaForSave = partialStep ? null : (schemaOverride ?? (
        ufWizardStep === UF_WIZARD_STEP.ATTRIBUTES ? ufAttributeSchema : null
      ));
      const listingCoreUserLotId = userLotData?.id || sharedUserLotId;
      if (collectionCanEdit && !listingCoreUserLotId) {
        toast.error({ title: 'Error', message: 'No se pudo guardar la unidad.' });
        return false;
      }
      const builtPayload = collectionCanEdit || guestCoreUpdate
        ? buildUserLotListingCorePayload(userLotForm, {
          lotId: editedInfo.id,
          userLotId: listingCoreUserLotId,
        })
        : buildUserLotPayload(userLotForm, {
          lotId: editedInfo.id,
          userLotId: userLotData?.id,
          schema: schemaForSave,
          partialStep,
          officialUnitNumber: userLotData?.id
            ? null
            : getOfficialUnitNumber(pendingOfficialUnit),
        });
      const payload = stripSpaceGuestUfPayload(builtPayload, writeOwnerUserId);
      const officialSurface = collectionCanEdit || guestCoreUpdate
        ? null
        : parseOfficialBuiltSurfaceM2(
          pendingOfficialUnit || getOfficialUnitFromUserLot(userLotData),
        );
      if (!partialStep && officialSurface != null) {
        payload.covered_surface = officialSurface;
      }
      const res = await createUserLot(payload, { ownerUserId: writeOwnerUserId });
      // Cambió el espacio mientras guardaba: no mezclar datos con el nuevo.
      if (isStaleSpace()) return false;
      const alreadyAnnotatedId = getAlreadyAnnotatedUserLotId(res);
      if (alreadyAnnotatedId) {
        toast.info({
          message: 'Ya tenías información en esta unidad. Abrimos la edición.',
        });
        setPendingOfficialUnit(null);
        setIsNewUnit(false);
        ufBuildingTypeDraftPendingRef.current = false;
        const detail = await handleSelectUnit(alreadyAnnotatedId);
        setUnitView('detail');
        if (detail) {
          setIsEditing(true);
          setUfWizardStep(resolveInitialWizardStep({
            isEditing: true,
            buildingType: detail.building_type,
          }));
          if (normalizeBuildingType(detail.building_type)) {
            await loadUfSchema(detail.building_type);
          }
        }
        reloadOfficialUnits();
        return true;
      }
      if (!res?._httpOk || res.error || !res.data) {
        // 400 VALIDATION_ERROR con `field` de la sección 090 → error en ese campo.
        const propertyErrorFieldId = res?.code === 'VALIDATION_ERROR'
          ? resolveUfPropertyErrorFieldId(res.field, schemaForSave || ufAttributeSchema)
          : null;
        if (propertyErrorFieldId) {
          setUserLotForm((prev) => ({
            ...prev,
            property_errors: { [propertyErrorFieldId]: extractApiErrorMessage(res, 'Valor inválido') },
          }));
        }
        toast.error({
          title: 'Error',
          message: resolveSpaceGuestWriteError(res?._httpStatus, writeOwnerUserId, {
            message: extractApiErrorMessage(res, ''),
            foreignListIds: res?.foreign_list_ids,
          }) || extractApiErrorMessage(res, 'No se pudo guardar la unidad'),
        });
        return false;
      }
      // Un guardado completo deja de ser el borrador solo-tipo. Si el flag quedaba
      // prendido, la unidad ya guardada seguía pidiendo confirmación al salir y el
      // efecto de cambio de lote la borraba como si fuera basura.
      readUfSaveWarnings(res).forEach((message) => toast.warning(message));
      if (!partialStep) {
        ufBuildingTypeDraftPendingRef.current = false;
      }
      const savedListIds = Array.isArray(res.data.list_ids)
        ? res.data.list_ids.filter(Boolean)
        : normalizedListIds;
      const savedId = res.data.id;
      // Permisos de la UF (mig 082) que manda la API; se completan con la fila
      // refrescada de `getuserlotunits` (el alta no trae `can_edit_all`).
      let permissionFields = {
        ...pickUfPermissionFields(
          userLotData?.id && String(userLotData.id) === String(savedId) ? userLotData : null,
        ),
        ...pickUfPermissionFields(res.data),
      };
      // createuserlot no trae images/videos/attachments; esos viven en endpoints
      // aparte y solo en getuserlotdetail. Preservar con ?? [] pisaba el estado
      // local de los hijos (uploads) con [] y UserLotImages/Videos/Attachments
      // tratan [] como autoritativo (no refetch) → detalle vacío hasta reabrir.
      const pickMediaFromSave = (key) => {
        const remote = res.data?.[key];
        const local = userLotData?.[key];
        if (Array.isArray(remote) && remote.length > 0) return remote;
        if (Array.isArray(local) && local.length > 0) return local;
        if (Array.isArray(remote)) return remote;
        // undefined (no []) → los hijos hacen fetch en vez de mostrar vacío stale
        return undefined;
      };
      const buildSavedUserLotData = (mediaSource = null) => ({
        ...userLotForm,
        ...permissionFields,
        ...(mediaSource && typeof mediaSource === 'object' ? mediaSource : {}),
        id: savedId,
        owner_id: (mediaSource && mediaSource.owner_id) ?? res.data.owner_id
          ?? (writeOwnerUserId || currentUserId),
        list_ids: Array.isArray(mediaSource?.list_ids)
          ? mediaSource.list_ids.filter(Boolean)
          : savedListIds,
        lists: Array.isArray(mediaSource?.lists)
          ? mediaSource.lists
          : (Array.isArray(res.data.lists) ? res.data.lists : userLotData?.lists),
        images: mediaSource
          ? mediaSource.images
          : pickMediaFromSave('images'),
        videos: mediaSource
          ? mediaSource.videos
          : pickMediaFromSave('videos'),
        attachments: mediaSource
          ? mediaSource.attachments
          : pickMediaFromSave('attachments'),
        official_unit: mediaSource?.official_unit
          ?? res.data.official_unit
          ?? userLotData?.official_unit
          ?? pendingOfficialUnit
          ?? null,
      });
      const wasNew = isNewUnit;
      // El wizard crea el borrador en el paso 1, así que en el Guardar final
      // `isNewUnit` ya es false: el ref recuerda que es un alta de esta sesión.
      const createdInWizard = wasNew || ufCreatedInWizardRef.current;
      if (isNewUnit) {
        setIsNewUnit(false);
        setSelectedUnitId(savedId);
      }
      if (res.data.official_unit) {
        setPendingOfficialUnit(null);
      }
      // Refrescar la lista de unidades para mantener el selector actualizado
      let refreshedUnits = units;
      try {
        // F2: refrescar con el mismo owner (UF del owner, no las del invitado).
        const unitsRes = await getUserLotUnits(editedInfo.id, { ownerUserId: writeOwnerUserId });
        if (isStaleSpace()) return false;
        if (Array.isArray(unitsRes?.data)) {
          const nextUnits = filterUnitsByListId(unitsRes.data, restrictToListId);
          setUnits(nextUnits);
          refreshedUnits = nextUnits;
          const savedRow = unitsRes.data.find((row) => String(row?.id) === String(savedId));
          permissionFields = { ...permissionFields, ...pickUfPermissionFields(savedRow) };
        }
      } catch (_) {}
      // Invitado sin flags todavía (refresh falló): pedirlos al detalle. Sin
      // `can_edit_all` el formulario caería a la regla acotada a mitad del wizard.
      if (writeOwnerUserId && savedId && editedInfo?.id
        && typeof permissionFields.can_edit_all !== 'boolean') {
        try {
          const flagsRes = await getUserLotDetail(editedInfo.id, savedId, {
            ownerUserId: writeOwnerUserId,
          });
          if (isStaleSpace()) return false;
          permissionFields = { ...permissionFields, ...pickUfPermissionFields(flagsRes?.data) };
        } catch (_) {}
      }
      if (pendingOfficialUnit || res.data.official_unit || officialUnits.length > 0) {
        reloadOfficialUnits();
      }
      const hasListAfterSave = refreshedUnits.length >= 1 || officialUnits.length >= 1;
      // Tras el Guardar final (alta o edición, dueño o invitado, cualquier
      // origen) vuelve al listado de UF, ya refrescado, y resalta la guardada.
      // «Continuar» del wizard (paso Publicación) no sale: sigue en el form.
      const leaveToList = shouldLeaveToUnitListAfterSave({
        finalSave,
        createdInWizard,
        partialStep,
        hasListAfterSave,
        wizardStep: ufWizardStep,
      });
      if (leaveToList) {
        ufCreatedInWizardRef.current = false;
        setHighlightUnitId(savedId);
        setUnitView('list');
        setIsEditing(false);
        setUserLotData(null);
        setSelectedUnitId(null);
        setPendingOfficialUnit(null);
        setUfWizardStep(UF_WIZARD_STEP.BUILDING_TYPE);
        setUfAttributeSchema(null);
      } else if (!partialStep && savedId && editedInfo?.id) {
        // Guardado completo quedándose en detalle: hidratar media desde detail
        try {
          const detailRes = await getUserLotDetail(editedInfo.id, savedId, {
            ownerUserId: writeOwnerUserId,
          });
          if (isStaleSpace()) return false;
          if (detailRes?.data) {
            setUserLotData(buildSavedUserLotData(detailRes.data));
          } else {
            setUserLotData(buildSavedUserLotData(null));
          }
        } catch (detailErr) {
          console.error('Error refetching user lot detail after save:', detailErr);
          setUserLotData(buildSavedUserLotData(null));
        }
      } else {
        // partialStep (p.ej. solo building_type) o sin id: merge liviano
        setUserLotData(buildSavedUserLotData(null));
      }
      refreshUserLotLists();
      setUserLotChecked(true);
      if (ufWizardStep === UF_WIZARD_STEP.ATTRIBUTES && !leaveToList) {
        setIsEditing(false);
      }
      if (!silent) {
        toast.success({
          message: wasNew && ufWizardStep !== UF_WIZARD_STEP.ATTRIBUTES
            ? 'Progreso guardado'
            : (wasNew
              ? 'Unidad funcional creada correctamente'
              : 'Unidad funcional guardada correctamente'),
        });
      }
      return true;
    } catch (e) {
      console.error('Error saving user lot:', e);
      toast.error({
        title: 'Error',
        message: resolveSpaceGuestWriteError(e?._httpStatus ?? e?.status, writeOwnerUserId, {
          message: extractApiErrorMessage(e, ''),
          foreignListIds: e?.foreign_list_ids,
        }) || extractApiErrorMessage(e, 'No se pudo guardar la información'),
      });
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleUfBuildingTypeChange = useCallback(async (nextOption) => {
    // Opción del paso 1 (`resolveUfPropertyTypes`) o, por compat, un building_type suelto.
    const option = nextOption && typeof nextOption === 'object' ? nextOption : null;
    const nextType = option ? option.buildingType : nextOption;
    const nextPropertyType = option?.isPropertyType ? option.id : '';
    const normalizedNext = normalizeBuildingType(nextType);
    const currentType = normalizeBuildingType(userLotForm.building_type);
    if (!normalizedNext || normalizedNext === currentType) return;

    const hasAttributeValues = Boolean(currentType) && (
      Object.keys(userLotForm.attributes || {}).some((key) => userLotForm.attributes[key])
      || Object.keys(userLotForm).some((key) => (
        !['title', 'description', 'observations', 'unit_label', 'price', 'currency', 'operation', 'is_public', 'list_ids', 'building_type', 'attributes',
          'property_type', 'property_type_loaded', 'property_info', 'property_info_loaded', 'property_errors'].includes(key)
        && userLotForm[key] !== '' && userLotForm[key] !== false
      ))
    );

    if (hasAttributeValues) {
      const confirmed = await confirmDialog({
        title: '¿Cambiar tipo de inmueble?',
        description: 'Las características cargadas para el tipo anterior se van a reiniciar.',
        confirmLabel: 'Cambiar tipo',
        cancelLabel: 'Cancelar',
      });
      if (!confirmed?.isConfirmed) return;
    }

    setUserLotForm((prev) => applyOfficialBuiltSurfaceToForm({
      ...clearAttributeFormValues(prev),
      building_type: normalizedNext,
      property_type: nextPropertyType,
      attributes: {},
    }, pendingOfficialUnit || getOfficialUnitFromUserLot(userLotData)));
    await loadUfSchema(normalizedNext);
  }, [loadUfSchema, userLotForm, pendingOfficialUnit, userLotData]);

  const handleUfWizardContinue = useCallback(async () => {
    if (ufWizardStep === UF_WIZARD_STEP.BUILDING_TYPE) {
      if (!normalizeBuildingType(userLotForm.building_type)) {
        toast.error({ title: 'Falta el tipo', message: 'Elegí un tipo de inmueble para continuar.' });
        return;
      }
      const startedAsNew = isNewUnit || !userLotData?.id;
      // Mismo flujo en espacio ajeno (mig 082): el borrador se crea con
      // `owner_user_id` y el invitado lo puede completar y descartar.
      const draftOwnerUserId = spaceWriteOwnerUserId;
      // Avance optimista: el usuario ve el paso 2 con skeleton mientras se guarda.
      setUfWizardStep(UF_WIZARD_STEP.LISTING);
      setUfListingBootstrapLoading(true);
      try {
        const saved = await handleSaveUserLot({
          schemaOverride: null,
          partialStep: 'building_type',
          silent: true,
        });
        if (!saved) {
          setUfWizardStep(UF_WIZARD_STEP.BUILDING_TYPE);
          return;
        }
        if (startedAsNew) {
          ufBuildingTypeDraftPendingRef.current = true;
          ufDraftOwnerRef.current = draftOwnerUserId;
          ufCreatedInWizardRef.current = true;
        }
        await loadUfSchema(userLotForm.building_type);
      } finally {
        setUfListingBootstrapLoading(false);
      }
      return;
    }

    if (ufWizardStep === UF_WIZARD_STEP.LISTING) {
      if (!userLotForm.title?.trim()) {
        toast.error({ title: 'Falta el título', message: 'Completá el título antes de continuar.' });
        return;
      }
      const saved = await handleSaveUserLot({ schemaOverride: null });
      if (!saved && !userLotData?.id) return;
      const schema = ufAttributeSchema || await loadUfSchema(userLotForm.building_type);
      if (schema) {
        const detailSource = {
          ...(userLotData || {}),
          ...userLotForm,
          attributes: userLotForm.attributes || userLotData?.attributes || {},
        };
        setUserLotForm((prev) => applyOfficialBuiltSurfaceToForm({
          ...prev,
          ...mergeDetailIntoAttributeForm(detailSource, schema),
        }, pendingOfficialUnit || getOfficialUnitFromUserLot(userLotData)));
      }
      setUfWizardStep(UF_WIZARD_STEP.ATTRIBUTES);
    }
  // handleSaveUserLot es función estable por render; incluirla recrea el callback en cada tick
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ufWizardStep, userLotForm, userLotData, isNewUnit, loadUfSchema, ufAttributeSchema, pendingOfficialUnit, spaceWriteOwnerUserId]);

  const handleUfWizardBack = useCallback(() => {
    if (ufWizardStep === UF_WIZARD_STEP.ATTRIBUTES) {
      setUfWizardStep(UF_WIZARD_STEP.LISTING);
      return;
    }
    if (ufWizardStep === UF_WIZARD_STEP.LISTING) {
      // Volver al tipo: se conserva el borrador por si cambia building_type.
      setUfWizardStep(UF_WIZARD_STEP.BUILDING_TYPE);
    }
  }, [ufWizardStep]);

  /** Navegación libre a pasos ya visitados (click en el stepper). */
  const handleUfWizardGoToStep = useCallback((stepId) => {
    if (ufListingBootstrapLoading || isSaving) return;
    if (!Object.values(UF_WIZARD_STEP).includes(stepId)) return;
    // Solo hacia atrás: no saltear validaciones de Continuar.
    if (stepId >= ufWizardStep) return;
    setUfWizardStep(stepId);
  }, [ufWizardStep, ufListingBootstrapLoading, isSaving]);

  const handleUfEditCancel = useCallback(async () => {
    const { discarded, cancelled } = await confirmDiscardUfBuildingTypeDraftIfNeeded();
    if (cancelled) return;

    ufCreatedInWizardRef.current = false;
    setIsEditing(false);
    setUfListingBootstrapLoading(false);

    if (discarded || isNewUnit) {
      setUfWizardStep(UF_WIZARD_STEP.BUILDING_TYPE);
      setIsNewUnit(false);
      setPendingOfficialUnit(null);
      setUnitView('list');
      return;
    }

    setUfWizardStep(resolveInitialWizardStep({
      isEditing: false,
      buildingType: userLotForm.building_type,
    }));
    if (userLotData) {
      setUserLotForm(applyOfficialBuiltSurfaceToForm(
        mapUserLotToForm(userLotData),
        getOfficialUnitFromUserLot(userLotData) || pendingOfficialUnit,
      ));
    }
  }, [confirmDiscardUfBuildingTypeDraftIfNeeded, userLotData, userLotForm.building_type, isNewUnit, pendingOfficialUnit]);

  const handleUfEditStart = useCallback(() => {
    setIsEditing(true);
    setUfWizardStep(resolveInitialWizardStep({
      isEditing: true,
      buildingType: userLotForm.building_type,
    }));
    if (normalizeBuildingType(userLotForm.building_type)) {
      loadUfSchema(userLotForm.building_type);
    }
  }, [userLotForm.building_type, loadUfSchema]);

  const handleDeleteUserLot = async () => {
    if (!editedInfo?.id || !userLotData?.id) return;
    const isOfficialOverlay = Boolean(
      getOfficialUnitFromUserLot(userLotData) || pendingOfficialUnit,
    );
    await deleteOwnedUserLot(userLotData.id, { isOfficialOverlay });
  };

  const handleRemoveOfficialOverlay = (userLotId) => {
    if (!editedInfo?.id || !userLotId) return;
    return deleteOwnedUserLot(userLotId, { isOfficialOverlay: true });
  };

  const deleteOwnedUserLot = async (userLotId, { isOfficialOverlay = false } = {}) => {
    // Borrar UF: dueño, o invitado sobre una UF que creó él (`can_delete` de la
    // API, mig 082) con `owner_user_id`. Si no, no se ofrece (la API da 404).
    const writeOwnerUserId = spaceWriteOwnerUserId;
    if (isSpaceGuest) {
      const target = String(userLotData?.id) === String(userLotId)
        ? userLotData
        : units.find((row) => String(row?.id) === String(userLotId));
      if (!resolveSpaceGuestUnitAccess(target).canDelete) return;
    }
    const result = await confirmDialog({
      title: '¿Eliminar esta unidad?',
      description: isOfficialOverlay
        ? 'Se eliminará tu información (precio, fotos, atributos). La unidad catastral sigue en la lista.'
        : 'Se eliminará definitivamente tu información vinculada a este lote.',
      confirmLabel: 'Eliminar',
      cancelLabel: 'Cancelar',
      variant: 'danger',
    });
    if (!result?.isConfirmed) return;
    try {
      const res = await deleteUserLot(userLotId, { ownerUserId: writeOwnerUserId });
      if (!res.message) {
        toast.error({
          title: 'Error',
          message: resolveSpaceGuestWriteError(res?._httpStatus, writeOwnerUserId)
            || 'No se pudo eliminar la información.',
        });
        return;
      }
      // Cambió el espacio mientras borraba: el Dock ya se cerró, no mezclar.
      if (spaceWriteOwnerRef.current !== writeOwnerUserId) return;
      setIsEditing(false);
      setUserLotChecked(true);
      refreshUserLotLists();

      let remaining = [];
      try {
        const unitsRes = await getUserLotUnits(editedInfo.id, { ownerUserId: writeOwnerUserId });
        remaining = filterUnitsByListId(
          Array.isArray(unitsRes?.data) ? unitsRes.data : [],
          restrictToListId,
        );
        setUnits(remaining);
      } catch (_) {
        remaining = [];
        setUnits([]);
      }

      if (isOfficialOverlay) reloadOfficialUnits();

      setPendingOfficialUnit(null);
      setSelectedUnitId(null);
      setUserLotData(null);
      if (remaining.length > 0 || officialUnits.length > 0 || isOfficialOverlay) {
        setUnitView('list');
        if (remaining.length === 0) setUserLotForm(emptyUserLotForm());
      } else {
        setUserLotForm(emptyUserLotForm());
      }

      toast.success({
        title: 'Eliminado.',
        message: isOfficialOverlay
          ? 'La unidad catastral sigue disponible.'
          : 'La unidad fue eliminada correctamente.',
      });
    } catch (e) {
      console.error('Error deleting user lot:', e);
      toast.error({ title: 'Error', message: 'No se pudo eliminar la información.' });
    }
  };

  if (!editedInfo && !isLoading && !loadError && !polygonId) return null;

  const isHydratingPolygon = Boolean(polygonId) && !editedInfo;
  const showFetchingSkeleton = Boolean(isLoading) && !editedInfo;
  const showLoadError = Boolean(loadError) && !editedInfo && !polygonId && !isLoading;

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <PanelShell
      uiLeftInset={uiLeftInset}
      bottomInset={shellBottomInset}
      forceMobile={shellForceMobile}
      fullWidth={shellFullWidth}
      topInset={shellTopInset}
      notice={shellNotice}
      onClose={handlePanelClose}
    >
      <Paper
        elevation={0}
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          height: '100%',
          minHeight: '100%',
          bgcolor: PANEL_BG,
          backgroundImage: 'none',
          color: colors.textPrimary,
          boxShadow: '0 18px 45px rgba(15,23,42,0.55)',
          border: accentColor ? 'none' : `1px solid ${PANEL_BORDER}`,
          borderRadius: shellForceMobile ? 0 : { xs: 0, sm: '16px' },
        }}
      >
        {accentColor ? (
          <Box sx={{ height: 3, bgcolor: panelAccent, flexShrink: 0 }} />
        ) : null}
        <Box sx={{ bgcolor: PANEL_BG }}>
          <PanelHeader
            title="PROPIEDADES DEL LOTE"
            subtitle={listContextLabel || undefined}
            startAdornment={listContextLabel ? (
              <Tooltip title={`Volver a ${listContextLabel}`} placement="top" arrow>
                <IconButton
                  onClick={handlePanelClose}
                  size="small"
                  aria-label={`Volver a ${listContextLabel}`}
                  sx={dockHeaderIconButtonSx}
                >
                  <ArrowBackIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            ) : null}
            actions={(
              <>
                {inquiryFormProps ? (
                  <Button
                    type="button"
                    onClick={scrollToInquiry}
                    aria-label="Ir al formulario de consulta"
                    sx={showroomInquiryHeaderButtonSx(brandChipAccent)}
                  >
                    Consultar
                  </Button>
                ) : null}

                {editedInfo?.id && !readOnly && (
                  <>
                    <IconButton
                      onClick={handleMenuOpen}
                      size="small"
                      aria-label="Más acciones del lote"
                      aria-expanded={Boolean(menuAnchor)}
                      sx={dockActionMenuTriggerSx(Boolean(menuAnchor))}
                    >
                      <MoreVertIcon sx={{ fontSize: 20 }} />
                    </IconButton>

                    <Menu
                      anchorEl={menuAnchor}
                      open={Boolean(menuAnchor)}
                      onClose={handleMenuClose}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                      PaperProps={dockActionMenuPaperProps}
                      MenuListProps={ACTION_MENU_LIST_PROPS}
                    >
                      <ActionMenuItem
                        onClick={handleShare}
                        icon={<ShareIcon sx={{ fontSize: 20 }} />}
                        label="Compartir"
                      />

                      {currentUserId && !isSpaceGuest ? (
                        <ActionMenuItem
                          onClick={handleToggleFavoriteWithFeedback}
                          icon={<MarkerMenuDot active={isFavorite} />}
                          label={isFavorite ? 'Quitar marcador' : 'Agregar marcador'}
                        />
                      ) : null}

                      {currentUserId ? (
                        <ActionMenuItem
                          onClick={handleReportParcelIssue}
                          icon={<ErrorOutlineIcon sx={{ fontSize: 20 }} />}
                          label="Reportar error del lote"
                        />
                      ) : null}
                    </Menu>
                  </>
                )}

                {!publicLotView && !sharedUserLotToken && (
                  <IconButton onClick={handlePanelClose} size="small" aria-label="Cerrar panel" sx={panelCloseIconButtonSx}>
                    <CloseIcon />
                  </IconButton>
                )}
              </>
            )}
          />
        </Box>

        {/* Tabs */}
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            bgcolor: PANEL_BG,
            borderBottom: `1px solid ${panelHeaderDividerSx.borderColor}`,
            '& .MuiTab-root': {
              color: colors.textSecondary,
              textTransform: 'none',
              fontFamily: typography.fontFamily.primary,
              fontSize: '16px',
              lineHeight: '24px',
            },
            '& .MuiTab-root.Mui-selected': { color: panelAccent },
            '& .MuiTabs-indicator': { backgroundColor: panelAccent, height: 3 },
          }}
        >
          <Tab label="Lote" />
          {showDetailsTab && <Tab label="Unidades Funcionales" />}
          {/* <Tab label="Código" />        Se comenta temporalmente
        <Tab label="Atracciones" /> */}
        </Tabs>

        {/* Control de visualización del edificio en el mapa */}
        {editedInfo?.id && onLotBuildingDisplayModeChange && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 1.5,
              pt: 1.5,
              borderBottom: '1px solid rgba(255,255,255,0.06)',
              bgcolor: PANEL_BG,
              minHeight: 44,
            }}
          >
            <Typography sx={PANEL_SECTION_TITLE_SX}>
              VISTA
            </Typography>

            {!buildingVisualizationRequiresLogin && lotBuildingStatus === 'loading' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <CircularProgress size={11} sx={{ color: 'rgba(255,255,255,0.35)' }} />
                <Typography sx={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)', fontFamily: typography.fontFamily.primary }}>
                  Buscando…
                </Typography>
              </Box>
            )}

            {!buildingVisualizationRequiresLogin && lotBuildingStatus === 'unavailable' && !lotDetailLoading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <DomainDisabledIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.25)' }} />
                <Typography sx={{ fontSize: '11px', color: 'rgba(255,255,255,0.28)', fontFamily: typography.fontFamily.primary }}>
                  Aún no hay datos para este lote
                </Typography>
              </Box>
            )}

            {buildingVisualizationRequiresLogin && renderBuildingViewChips({
              disabled: true,
              ariaLabel: 'Vista del edificio en el mapa (requiere iniciar sesión)',
              accentColor: brandChipAccent,
            })}

            {!buildingVisualizationRequiresLogin && (lotBuildingStatus === 'available' || lotBuildingStatus === 'idle') && renderBuildingViewChips({
              value: lotBuildingDisplayMode,
              onChange: onLotBuildingDisplayModeChange,
              ariaLabel: 'Vista del edificio en el mapa',
              accentColor: brandChipAccent,
            })}

            {buildingVisualizationRequiresLogin ? (
              <Tooltip
                title="Iniciá sesión para ver la visualización del edificio."
                placement="top"
                arrow
                slotProps={{
                  tooltip: {
                    sx: {
                      textAlign: 'center',
                      maxWidth: 280,
                      whiteSpace: 'normal',
                    },
                  },
                }}
              >
                <InfoOutlinedIcon
                  sx={{
                    fontSize: 18,
                    color: 'rgba(255,255,255,0.35)',
                    ml: 'auto',
                    flexShrink: 0,
                    cursor: 'help',
                    '&:hover': { color: 'rgba(255,255,255,0.65)' },
                    transition: 'color 0.15s',
                  }}
                />
              </Tooltip>
            ) : lotBuildingDisplayMode !== 'hidden' && (
              <Tooltip
                title="Los modelos 3D y plantas son aproximaciones generadas automáticamente y pueden no reflejar con exactitud la geometría real del edificio."
                placement="top"
                arrow
                slotProps={{
                  tooltip: {
                    sx: {
                      textAlign: 'center',
                      maxWidth: 280,
                      whiteSpace: 'normal',
                    },
                  },
                }}
              >
                <InfoOutlinedIcon
                  sx={{
                    fontSize: 20,
                    color: 'rgba(255,255,255,0.25)',
                    ml: 'auto',
                    flexShrink: 0,
                    cursor: 'help',
                    '&:hover': { color: 'rgba(255,255,255,0.55)' },
                    transition: 'color 0.15s',
                  }}
                />
              </Tooltip>
            )}
          </Box>
        )}

        {/* Content */}
        <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0, bgcolor: PANEL_BG }}>
          {showFetchingSkeleton || isHydratingPolygon ? (
            <LoadingSkeleton tab={showDetailsTab && activeTab === 1 ? 'details' : 'lot'} />
          ) : showLoadError ? (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5, px: 3, textAlign: 'center' }}>
              <Typography variant="body2" sx={dockSecondaryTextSx}>{loadError}</Typography>
              <Typography variant="body2" sx={{ ...dockSecondaryTextSx, fontSize: '11px' }}>
                Intenta seleccionar otro lote o vuelve a intentarlo más tarde.
              </Typography>
            </Box>
          ) : showDetailsTab && activeTab === 1 && showUnitsDetailPane ? (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <DetailsTab
                lotId={editedInfo?.id}
                userLotData={userLotData}
                userLotForm={userLotForm}
                setUserLotForm={handleUserLotFormChange}
                loadingUserLot={loadingUserLot}
                userLotLists={userLotLists}
                onListCreated={refreshUserLotLists}
                isEditing={isEditing}
                isSaving={isSaving}
                isOwner={isOwner}
                spaceGuestCanEditAll={spaceGuestAccess.canEditAll}
                spaceGuestCanDelete={spaceGuestAccess.canDelete}
                canView={canView}
                canEdit={collectionCanEdit ? Boolean(userLotData?.id || sharedUserLotId) : canEdit}
                fieldSx={fieldSx}
                secondaryTextSx={dockSecondaryTextSx}
                onSave={() => handleSaveUserLot({ schemaOverride: ufAttributeSchema, finalSave: true })}
                onDelete={handleDeleteUserLot}
                onEditStart={() => setIsEditing(true)}
                onEditStartWithWizard={collectionCanEdit ? undefined : handleUfEditStart}
                onAddUnit={restrictToListId ? undefined : handleAddUnitFromList}
                recordLeads={recordLeadsNode}
                recordCampaigns={recordCampaignsNode}
                renderMarketComparison={renderUnitMarketComparison}
                onEditCancel={handleUfEditCancel}
                readOnly={readOnly}
                collectionCanEdit={collectionCanEdit}
                onBack={showUnitList ? handleBackToUnitList : undefined}
                ufWizardStep={ufWizardStep}
                ufBuildingTypes={ufBuildingTypes}
                ufAttributeSchema={ufAttributeSchema}
                ufSchemaLoading={ufSchemaLoading}
                ufListingBootstrapLoading={ufListingBootstrapLoading}
                onBuildingTypeChange={handleUfBuildingTypeChange}
                onWizardContinue={handleUfWizardContinue}
                onWizardBack={handleUfWizardBack}
                onWizardGoToStep={handleUfWizardGoToStep}
                accentColor={brandChipAccent}
                lotContext={editedInfo}
                officialUnit={detailOfficialUnit}
                bodyScrollRef={panelContentScrollRef}
                spaceOwnerUserId={spaceReadOwnerUserId}
                privateBlockTitle={privateBlockTitle}
              >
                {inquiryFormProps && (
                  <Box sx={{ pt: 0.5 }}>
                    <CollectionInquiryForm
                      manage={inquiryManage}
                      {...inquiryFormProps}
                      accentColor={brandChipAccent}
                      />
                  </Box>
                )}
              </DetailsTab>
            </Box>
          ) : (
            <Box
              ref={setPanelContentScrollNode}
              sx={{
                flex: 1,
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                scrollbarGutter: 'stable',
                ...DOCK_SCROLLBAR_SX,
                ...getScrollEdgeFadeSx(fadeTop, fadeBottom),
              }}
            >
              {activeTab === 0 && (
                lotDetailLoading ? (
                  <LoadingSkeleton tab="lot" />
                ) : (
                  <LotInfoTab
                    editedInfo={editedInfo}
                    lotId={editedInfo?.id}
                    canEdit={!!currentUserId && (!readOnly || collectionCanEdit)}
                    listingCoreOnly={collectionCanEdit}
                    fieldSx={dockTextFieldSx}
                    secondaryTextSx={dockSecondaryTextSx}
                    userLotLists={userLotLists}
                    onListCreated={refreshUserLotLists}
                    sharedLotUserContent={readOnly && (sharedUserLotToken || sharedToken)
                      ? sharedLotUserContent
                      : null}
                    maskSensitive={shouldMaskSensitive}
                    initialOpenLotContent={initialOpenLotContent}
                    onInitialOpenLotContentConsumed={onInitialOpenLotContentConsumed}
                    accentColor={brandChipAccent}
                    spaceOwnerUserId={spaceReadOwnerUserId}
                    privateBlockTitle={privateBlockTitle}
                    recordLeads={lotRecordLeadsNode}
                    recordCampaigns={lotRecordCampaignsNode}
                    renderMarketComparison={renderLotMarketComparison}
                  />
                )
              )}

              {showDetailsTab ? (
                <>
                  {activeTab === 1 && showUnitsListPane && (
                    showUnitsListSkeleton && (!showUnitList || waitingOfficialUnits) ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                        <CircularProgress sx={{ color: '#ffffff' }} />
                      </Box>
                    ) : (
                      <UnitsTabList
                        officialUnits={officialUnits}
                        userUnits={unitsForList}
                        commercialRollup={canLoadCommercial ? unitsCommercial?.rollup || null : null}
                        commercialLotSummary={canLoadCommercial ? unitsCommercial?.lot || null : null}
                        userLotLists={userLotLists}
                        canEdit={!!currentUserId && !readOnly}
                        readOnly={readOnly}
                        hideListFilter={!!restrictToListId || readOnly}
                        showAddUnit={!restrictToListId && !readOnly}
                        accentColor={brandChipAccent}
                        onSelectUnit={handleSelectUnitFromList}
                        onAddOwnUnit={restrictToListId ? undefined : handleAddUnitFromList}
                        onCargarInfo={handleAnnotateOfficialUnit}
                        onEditOverlay={handleEditOfficialOverlay}
                        onRemoveOverlay={handleRemoveOfficialOverlay}
                        removeOverlayRequiresCanDelete={isSpaceGuest}
                        highlightUnitId={highlightUnitId}
                      />
                    )
                  )}
                  {activeTab === 3 && <NearbyAttractionsTab secondaryTextSx={dockSecondaryTextSx} />}
                </>
              ) : (
                <>
                  {activeTab === 2 && <NearbyAttractionsTab secondaryTextSx={dockSecondaryTextSx} />}
                </>
              )}

              {inquiryFormProps && (
                <Box sx={{ px: 2, pb: 2, pt: 0.5 }}>
                  <CollectionInquiryForm
                    manage={inquiryManage}
                    {...inquiryFormProps}
                    accentColor={brandChipAccent}
                    />
                </Box>
              )}
            </Box>
          )}
        </Box>

        {/* Key con prefijo: con otros diálogos hermanos en el mismo Paper, la
            misma key (sub del owner / 'own-space') rompe la reconciliación. */}
        <ShareLotDialog
          key={`share-lot:${spaceWriteOwnerUserId || 'own-space'}`}
          open={shareLotDialogOpen}
          ownerUserId={spaceWriteOwnerUserId}
          lotId={editedInfo?.id || ''}
          lotName={editedInfo?.name || editedInfo?.id || ''}
          provinceName={editedInfo?.province || polygonProvince || ''}
          departmentName={editedInfo?.department || polygonDepartment || ''}
          initialUnits={units.length > 0 ? units : null}
          initialSelectedUnitIds={sharePreselectedUnitIds}
          onClose={() => {
            setShareLotDialogOpen(false);
            setSharePreselectedUnitIds([]);
          }}
        />

        <FeedbackModal
          isOpen={parcelFeedbackOpen}
          onClose={() => setParcelFeedbackOpen(false)}
          lotId={editedInfo?.id || null}
        />
      </Paper>
    </PanelShell>
  );
};

export default DockPanel;